import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import {
  createReservation,
  createReservationTable,
  fetchNextReservationTableNumber,
  deleteReservationTable,
  fetchReservationAvailability,
  fetchReservations,
  fetchReservationTables,
  saveReservationLayout,
  updateReservation,
  updateReservationArchive,
  updateReservationTable,
} from "../../../services/ReservationService";
import TableShapeIllustration, {
  DEFAULT_TABLE_COLOR,
} from "./TableShapeIllustration";
import ReservationTablePicker from "./ReservationTablePicker";
import ReservationCalendar from "./ReservationCalendar";
import { fetchRestoDataByUser } from "../../../services/restaurantService";
import { logAdminHistoryAction } from "../../../services/AdminHistoryService";
import { archivePlanFloorSetup } from "../../../utils/planFloorSetupArchiveStorage";
import { fetchConnectedUser } from "../../../services/OrdersService";
import socket from "../../../services/socket";
import "./Reservations.css";

const TABLE_COLOR_PRESETS = [
  "#C49A6C",
  "#8B5E3C",
  "#D4A574",
  "#6B4423",
  "#A67C52",
  "#B85C38",
  "#5C6B4A",
  "#4A6FA5",
  "#F47F72",
  "#6B7280",
];

const normalizeTableHex = (hex, fallback = DEFAULT_TABLE_COLOR) => {
  if (hex == null || typeof hex !== "string") return fallback;
  let value = hex.trim();
  if (!value) return fallback;
  if (!value.startsWith("#")) value = `#${value}`;
  if (/^#[0-9A-F]{3}$/i.test(value)) {
    value = `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  if (/^#[0-9A-F]{6}$/i.test(value)) return value.toUpperCase();
  return fallback;
};

const RESERVATION_STATUS_OPTIONS = [
  "pending",
  "confirmed",
  "arrived",
  "completed",
  "cancelled",
  "no-show",
];

const ACTIVE_RESERVATION_STATUSES = ["pending", "confirmed", "arrived"];
const RESERVATION_LIST_PAGE_SIZE = 5;

const getReservationListVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const pages = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  if (start > 2) pages.push("start-ellipsis");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push("end-ellipsis");
  pages.push(totalPages);
  return pages;
};

const getReservationId = (reservation) =>
  reservation?._id ? String(reservation._id) : "";

const getReservationTableId = (reservation) => {
  if (!reservation?.table) return "";
  return String(reservation.table._id || reservation.table);
};

const getReservationTimeWindow = (reservation, overrides = {}) => {
  const hasOverrides =
    overrides.startAt ||
    overrides.date ||
    overrides.time ||
    overrides.durationMinutes !== undefined;

  if (!hasOverrides && reservation?.startAt && reservation?.endAt) {
    const start = new Date(reservation.startAt);
    const end = new Date(reservation.endAt);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      return {
        start,
        end,
        durationMinutes: Math.max(
          15,
          Math.round((end.getTime() - start.getTime()) / 60000)
        ),
      };
    }
  }

  const durationMinutes = Math.max(
    15,
    Number(
      overrides.durationMinutes ??
        (reservation?.startAt && reservation?.endAt
          ? Math.round(
              (new Date(reservation.endAt).getTime() -
                new Date(reservation.startAt).getTime()) /
                60000
            )
          : 90)
    ) || 90
  );

  let start = null;
  if (overrides.startAt) {
    start = new Date(overrides.startAt);
  } else if (overrides.date && overrides.time) {
    const time = String(overrides.time);
    start = new Date(
      `${overrides.date}T${time.length === 5 ? `${time}:00` : time}`
    );
  } else if (reservation?.startAt) {
    start = new Date(reservation.startAt);
  }

  if (!start || Number.isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  return { start, end, durationMinutes };
};

const rangesOverlap = (startA, endA, startB, endB) =>
  startA < endB && endA > startB;

const isTableBookedAtTime = (
  tableId,
  window,
  reservations,
  excludeReservationId = ""
) => {
  if (!tableId || !window?.start || !window?.end) return false;
  const targetTableId = String(tableId);
  const excludeId = excludeReservationId ? String(excludeReservationId) : "";

  return reservations.some((item) => {
    if (!item || (excludeId && String(item._id) === excludeId)) return false;
    if (!ACTIVE_RESERVATION_STATUSES.includes(item.status)) return false;
    if (item.archived) return false;
    if (getReservationTableId(item) !== targetTableId) return false;

    const itemWindow = getReservationTimeWindow(item);
    if (!itemWindow?.start || !itemWindow?.end) return false;

    return rangesOverlap(
      window.start,
      window.end,
      itemWindow.start,
      itemWindow.end
    );
  });
};

const isTableLockedForSlot = (table) => table?.availability === "booked";

const buildWhatsAppLink = (phone, message) => {
  const cleanPhone = String(phone || "").replace(/[^\d+]/g, "");
  if (!cleanPhone) return "";
  return `https://wa.me/${cleanPhone.replace(/^\+/, "")}?text=${encodeURIComponent(
    message || ""
  )}`;
};

const buildGmailComposeLink = (email, subject, body) => {
  const clean = String(email || "").trim();
  if (!clean) return "";
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: clean,
  });
  if (subject) params.set("su", subject);
  if (body) params.set("body", body);
  return `https://mail.google.com/mail/?${params.toString()}`;
};

const buildClientReservationReplyContent = (reservation, t) => {
  const name = String(reservation?.customerName || "").trim() || t("reservationCustomer");
  const start = reservation?.startAt ? new Date(reservation.startAt) : null;
  const validStart = start && !Number.isNaN(start.getTime());
  const when = validStart
    ? t("reservationReplyWhenSuffix", {
        when: `${start.toLocaleDateString([], {
          weekday: "short",
          day: "numeric",
          month: "short",
        })} ${start.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`,
      })
    : "";
  return {
    subject: t("reservationReplyEmailSubject"),
    emailBody: t("reservationReplyEmailBody", { name, when }),
    whatsappMessage: t("reservationReplyWhatsAppBody", { name, when }),
  };
};

const today = () => new Date().toISOString().slice(0, 10);

const currentSystemTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`;
};

const createDefaultFilters = () => ({
  date: today(),
  time: currentSystemTime(),
  durationMinutes: 90,
  partySize: 2,
});

const createDefaultNewReservationForm = (overrides = {}) => ({
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  preferredContact: "phone",
  date: today(),
  time: currentSystemTime(),
  durationMinutes: 90,
  partySize: 2,
  table: "",
  seatingPreference: "",
  clientNotes: "",
  ...overrides,
});

const isValidReservationPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
};

const isValidReservationEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const RequiredMark = () => (
  <span className="reservation-required-mark" aria-hidden="true">
    *
  </span>
);

const tableShapeOptions = [
  { value: "round", labelKey: "reservationShapeRound", width: 88, height: 88, seats: 2 },
  { value: "square", labelKey: "reservationShapeSquare", width: 92, height: 92, seats: 4 },
  { value: "rectangle", labelKey: "reservationShapeRectangle", width: 132, height: 82, seats: 6 },
  { value: "oval", labelKey: "reservationShapeOval", width: 138, height: 86, seats: 6 },
  { value: "booth", labelKey: "reservationShapeBooth", width: 128, height: 92, seats: 4 },
  { value: "bar", labelKey: "reservationShapeBar", width: 156, height: 58, seats: 4 },
];

const getTableShapeLabelKey = (shape) =>
  tableShapeOptions.find((option) => option.value === shape)?.labelKey ||
  "reservationShapeRound";

const TableShapePreview = ({ shape, seats, baseColor, className = "" }) => (
  <span className={`reservation-toolbox-shape__preview is-${shape} ${className}`.trim()}>
    <TableShapeIllustration
      shape={shape}
      seats={seats}
      status="available"
      baseColor={baseColor}
    />
  </span>
);

const TABLE_RENDER_SCALE = 0.78;
const MIN_TABLE_SIZE = 56;
const MAX_TABLE_SIZE = 320;
const TABLE_POPUP_BODY_CLASS = "reservation-table-popup-active";
const DOOR_STORAGE_KEY = "reservationPlanDoorPosition";
const FLOOR_ELEMENTS_STORAGE_KEY = "reservationPlanFloorElements";
const FLOOR_CONFIG_STORAGE_KEY = "reservationPlanFloors";
const FLOOR_LAYOUTS_STORAGE_KEY = "reservationPlanLayouts";
const FLOOR_SELECTION_STORAGE_KEY = "reservationPlanSelectedFloorSalle";
const FLOOR_DISPOSITIONS_STORAGE_KEY = "reservationPlanDispositions";
const FLOOR_FOLDERS_STORAGE_KEY = "reservationPlanFolders";
const FLOOR_DEFAULT_LAYOUTS_STORAGE_KEY = "reservationPlanDefaultLayouts";
const MAX_DISPOSITIONS_PER_SALLE = 5;
const MAX_FOLDERS_PER_SALLE = 8;
const DEFAULT_FLOOR_ID = "floor-main";
const DEFAULT_SALLE_ID = "salle-main";
const DEFAULT_DOOR_POSITION = { x: 2.2, y: 42 };
const DEFAULT_LAYOUT_ACTIVE_ID = "default-layout";

const normalizeTableId = (value) => String(value ?? "").trim();

const buildTableLayoutMap = (entries = []) =>
  new Map(
    entries.map((entry) => [
      normalizeTableId(entry.tableId ?? entry._id ?? entry.id),
      entry,
    ])
  );

const readDefaultLayoutsStore = () => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(FLOOR_DEFAULT_LAYOUTS_STORAGE_KEY) || "{}"
    );
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeDefaultLayoutsStore = (store) => {
  localStorage.setItem(FLOOR_DEFAULT_LAYOUTS_STORAGE_KEY, JSON.stringify(store));
};

const isPlanFolderEntry = (entry) =>
  Boolean(
    entry?.id &&
      (String(entry.id).startsWith("folder-") || entry?.isFolder === true)
  );

const normalizeDispositionList = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list
    .filter((disposition) => !disposition?.isDefault && !isPlanFolderEntry(disposition))
    .map((disposition, index) => ({
      ...disposition,
      isDefault: false,
      isFolder: false,
      name: disposition?.name || `Disposition ${index + 1}`,
    }));
};

const TABLE_RESIZE_HANDLES = [
  { key: "nw", x: -1, y: -1 },
  { key: "n", x: 0, y: -1 },
  { key: "ne", x: 1, y: -1 },
  { key: "e", x: 1, y: 0 },
  { key: "se", x: 1, y: 1 },
  { key: "s", x: 0, y: 1 },
  { key: "sw", x: -1, y: 1 },
  { key: "w", x: -1, y: 0 },
];

const getTableResizeCursor = (handleKey, rotation = 0) => {
  const isRotated = Math.abs(Number(rotation) % 180) === 90;
  const cursors = {
    n: isRotated ? "ew-resize" : "ns-resize",
    s: isRotated ? "ew-resize" : "ns-resize",
    e: isRotated ? "ns-resize" : "ew-resize",
    w: isRotated ? "ns-resize" : "ew-resize",
    nw: isRotated ? "nesw-resize" : "nwse-resize",
    se: isRotated ? "nesw-resize" : "nwse-resize",
    ne: isRotated ? "nwse-resize" : "nesw-resize",
    sw: isRotated ? "nwse-resize" : "nesw-resize",
  };
  return cursors[handleKey] || "nwse-resize";
};

const createDefaultFloors = () => [
  {
    id: DEFAULT_FLOOR_ID,
    name: "Floor 1",
    salles: [{ id: DEFAULT_SALLE_ID, name: "Room 1" }],
  },
];

const looksLikeEntityId = (value, kind = "") => {
  const text = String(value || "").trim();
  if (!text) return true;
  if (kind === "floor") return /^floor-/i.test(text);
  if (kind === "salle") return /^salle-/i.test(text);
  return /^floor-/i.test(text) || /^salle-/i.test(text);
};

const normalizeFloorsConfig = (floorsList = [], floorLabel = "Floor", salleLabel = "Room") => {
  const list = Array.isArray(floorsList) ? floorsList : [];
  return list.map((floor, floorIndex) => {
    const floorName = String(floor?.name || "").trim();
    const safeFloorName =
      floorName && !looksLikeEntityId(floorName, "floor")
        ? floorName
        : `${floorLabel} ${floorIndex + 1}`;
    const salles = Array.isArray(floor?.salles) ? floor.salles : [];
    return {
      ...floor,
      id: floor?.id || `floor-${floorIndex + 1}`,
      name: safeFloorName,
      salles: salles.map((salle, salleIndex) => {
        const salleName = String(salle?.name || "").trim();
        const safeSalleName =
          salleName && !looksLikeEntityId(salleName, "salle")
            ? salleName
            : `${salleLabel} ${salleIndex + 1}`;
        return {
          ...salle,
          id: salle?.id || `salle-${salleIndex + 1}`,
          name: safeSalleName,
        };
      }),
    };
  });
};

const floorElementOptions = [
  { type: "wall", labelKey: "reservationWall", icon: "fas fa-grip-lines-vertical" },
  { type: "wc", labelKey: "reservationWc", icon: "fas fa-restroom" },
  { type: "cashier", labelKey: "reservationCashier", icon: "fas fa-cash-register" },
];

const normalizeFloorElements = (elements) =>
  Array.isArray(elements)
    ? elements.filter(
        (element) =>
          element?.id &&
          floorElementOptions.some((option) => option.type === element.type) &&
          Number.isFinite(element.position?.x) &&
          Number.isFinite(element.position?.y)
      )
    : [];

const buildLayoutSnapshot = (displayTables, doorPosition, floorElements) => ({
  savedAt: new Date().toISOString(),
  tables: displayTables.map((table) => ({
    tableId: normalizeTableId(table._id),
    position: {
      x: Number(table.position?.x ?? 50),
      y: Number(table.position?.y ?? 50),
    },
    size: {
      width: Number(table.size?.width ?? 88),
      height: Number(table.size?.height ?? 88),
    },
    rotation: Number(table.rotation) || 0,
  })),
  doorPosition: doorPosition || DEFAULT_DOOR_POSITION,
  floorElements: normalizeFloorElements(floorElements || []),
});

const normalizeFolderList = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list
    .filter((folder) => folder?.id && folder?.name)
    .map((folder) => ({
      ...folder,
      isFolder: true,
      name: String(folder.name).trim(),
      tables: Array.isArray(folder.tables) ? folder.tables : [],
      doorPosition: folder.doorPosition || DEFAULT_DOOR_POSITION,
      floorElements: normalizeFloorElements(folder.floorElements || []),
    }));
};

/** Move folder-* entries out of the old dispositions store into folders store. */
const splitFoldersFromDispositions = (dispositionMap = {}, folderMap = {}) => {
  const nextDispositions = {};
  const nextFolders = {
    ...(folderMap && typeof folderMap === "object" ? folderMap : {}),
  };

  Object.entries(dispositionMap || {}).forEach(([layoutKey, list]) => {
    const versions = [];
    const folders = [...(nextFolders[layoutKey] || [])];
    (Array.isArray(list) ? list : []).forEach((entry) => {
      if (isPlanFolderEntry(entry)) {
        if (!folders.some((folder) => folder.id === entry.id)) {
          folders.push({ ...entry, isFolder: true });
        }
      } else if (!entry?.isDefault) {
        versions.push(entry);
      }
    });
    nextDispositions[layoutKey] = normalizeDispositionList(versions);
    nextFolders[layoutKey] = normalizeFolderList(folders);
  });

  // Also normalize any layout keys that only exist in folderMap
  Object.keys(nextFolders).forEach((layoutKey) => {
    nextFolders[layoutKey] = normalizeFolderList(nextFolders[layoutKey]);
  });

  return { dispositions: nextDispositions, folders: nextFolders };
};

const migrateLegacyDefaultLayouts = (dispositionMap = {}) => {
  const defaultStore = readDefaultLayoutsStore();
  let changed = false;

  Object.entries(dispositionMap).forEach(([layoutKey, list]) => {
    if (defaultStore[layoutKey]?.tables?.length) return;
    const legacyDefault = (Array.isArray(list) ? list : []).find(
      (disposition) => disposition?.isDefault
    );
    if (!legacyDefault?.tables?.length) return;

    defaultStore[layoutKey] = {
      savedAt: legacyDefault.createdAt || new Date().toISOString(),
      tables: legacyDefault.tables.map((table) => ({
        ...table,
        tableId: normalizeTableId(table.tableId ?? table._id ?? table.id),
      })),
      doorPosition: legacyDefault.doorPosition || DEFAULT_DOOR_POSITION,
      floorElements: normalizeFloorElements(legacyDefault.floorElements || []),
    };
    changed = true;
  });

  if (changed) {
    writeDefaultLayoutsStore(defaultStore);
  }

  return Object.fromEntries(
    Object.entries(dispositionMap).map(([layoutKey, list]) => [
      layoutKey,
      normalizeDispositionList(list),
    ])
  );
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const LEGACY_NOTE_META =
  /^(Seating:\s*(indoor|outdoor|terrace)|Preferred contact:\s*(phone|whatsapp|email)|Country:\s*.+)$/i;

const getClientNoteOnly = (reservation) => {
  if (String(reservation?.clientNotes || "").trim()) {
    return String(reservation.clientNotes).trim();
  }
  const notes = String(reservation?.notes || "").trim();
  if (!notes) return "";
  return notes
    .split(" · ")
    .map((part) => part.trim())
    .filter((part) => part && !LEGACY_NOTE_META.test(part))
    .join(" · ")
    .trim();
};

const DURATION_OPTIONS = [60, 90, 120, 180];
const ACTUAL_DURATION_OPTIONS = [30, 45, 60, 75, 90, 105, 120, 150, 180];

const formatDurationLabel = (minutes) => {
  const value = Math.max(0, Number(minutes) || 0);
  if (!value) return "—";
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
};

const getPlannedDurationMinutes = (reservation) => {
  const planned = Number(reservation?.plannedDurationMinutes);
  if (Number.isFinite(planned) && planned >= 15) return planned;
  if (!reservation?.startAt || !reservation?.endAt) return null;
  const start = new Date(reservation.startAt);
  const end = new Date(reservation.endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000));
};

const getActualDurationMinutes = (reservation) => {
  const actual = Number(reservation?.actualDurationMinutes);
  return Number.isFinite(actual) && actual >= 15 ? actual : null;
};

const reservationToEditForm = (reservation) => {
  const start = reservation?.startAt ? new Date(reservation.startAt) : new Date();
  const end = reservation?.endAt ? new Date(reservation.endAt) : null;
  const validStart = !Number.isNaN(start.getTime());
  const durationMinutes =
    getPlannedDurationMinutes(reservation) ||
    (end && validStart && !Number.isNaN(end.getTime())
      ? Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000))
      : 90);

  return {
    date: validStart
      ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`
      : today(),
    time: validStart
      ? `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`
      : currentSystemTime(),
    durationMinutes,
    actualDurationMinutes: getActualDurationMinutes(reservation) ?? "",
    partySize: Math.max(1, Number(reservation?.partySize) || 2),
    customerName: reservation?.customerName || "",
    customerPhone: reservation?.customerPhone || "",
    customerEmail: reservation?.customerEmail || "",
    preferredContact: reservation?.preferredContact || "phone",
    seatingPreference: reservation?.seatingPreference || "",
    table: reservation?.table?._id || reservation?.table || "",
    status: reservation?.status || "pending",
    clientNotes: getClientNoteOnly(reservation),
  };
};

const Reservations = () => {
  const { t } = useTranslation();
  const floorRef = useRef(null);
  const dragState = useRef(null);
  const didDragRef = useRef(false);
  const draggedElementRef = useRef(null);
  const dragListenersAttachedRef = useRef(false);
  const handleWindowPointerMoveRef = useRef(null);
  const handleWindowPointerUpRef = useRef(null);
  const didLoadFloorConfig = useRef(false);
  const [filters, setFilters] = useState(createDefaultFilters);
  const [tables, setTables] = useState([]);
  const [availabilityTables, setAvailabilityTables] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [popupTableId, setPopupTableId] = useState(null);
  const [popupName, setPopupName] = useState("");
  const [popupSeats, setPopupSeats] = useState(2);
  const [popupSize, setPopupSize] = useState({ width: 88, height: 88 });
  const [popupColor, setPopupColor] = useState(DEFAULT_TABLE_COLOR);
  const [loading, setLoading] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const [showReservationsList, setShowReservationsList] = useState(false);
  const [showReservationsCalendar, setShowReservationsCalendar] = useState(false);
  const [newReservationForm, setNewReservationForm] = useState(createDefaultNewReservationForm);
  const [newReservationErrors, setNewReservationErrors] = useState({});
  const [submittingNewReservation, setSubmittingNewReservation] = useState(false);
  const [showNewReservationPopup, setShowNewReservationPopup] = useState(false);
  const [newReservationLockedTableId, setNewReservationLockedTableId] = useState("");
  const [reservations, setReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [detailsReservation, setDetailsReservation] = useState(null);
  const [editReservation, setEditReservation] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [archivingReservationId, setArchivingReservationId] = useState(null);
  const [updatingReservationId, setUpdatingReservationId] = useState(null);
  const [submittingTable, setSubmittingTable] = useState(false);
  const [submittingPopup, setSubmittingPopup] = useState(false);
  const [floors, setFloors] = useState(createDefaultFloors);
  const [floorsConfigReady, setFloorsConfigReady] = useState(false);
  const [selectedFloorId, setSelectedFloorId] = useState(DEFAULT_FLOOR_ID);
  const [selectedSalleId, setSelectedSalleId] = useState(DEFAULT_SALLE_ID);
  const [newFloorName, setNewFloorName] = useState("");
  const [newSalleName, setNewSalleName] = useState("");
  const [doorPosition, setDoorPosition] = useState(DEFAULT_DOOR_POSITION);
  const [floorElements, setFloorElements] = useState([]);
  const [isPlanFullscreen, setIsPlanFullscreen] = useState(false);
  const [savedDispositions, setSavedDispositions] = useState({});
  const [planFolders, setPlanFolders] = useState({});
  const [activeDispositionId, setActiveDispositionId] = useState("");
  const [activeFolderId, setActiveFolderId] = useState("");
  const [folderBrowseFloorId, setFolderBrowseFloorId] = useState("");
  const [folderBrowseSalleId, setFolderBrowseSalleId] = useState("");
  const [unplacedTableIds, setUnplacedTableIds] = useState([]);
  const [defaultLayoutRevision, setDefaultLayoutRevision] = useState(0);
  const foldersReadyRef = useRef(false);
  const dispositionsReadyRef = useRef(false);
  const pendingFolderOpenRef = useRef(null);
  const [listSearch, setListSearch] = useState("");
  const [listStatusFilter, setListStatusFilter] = useState("");
  const [listPage, setListPage] = useState(1);
  const [defaultTableColor, setDefaultTableColor] = useState(DEFAULT_TABLE_COLOR);
  const [affectPicker, setAffectPicker] = useState(null);

  const resolveTableColor = (table) =>
    normalizeTableHex(table?.color || defaultTableColor, defaultTableColor);

  const availabilityMap = useMemo(() => {
    return availabilityTables.reduce((acc, table) => {
      acc[table._id] = table;
      return acc;
    }, {});
  }, [availabilityTables]);

  const selectedFloor = useMemo(
    () => floors.find((floor) => floor.id === selectedFloorId) || floors[0],
    [floors, selectedFloorId]
  );

  const selectedSalle = useMemo(
    () =>
      selectedFloor?.salles?.find((salle) => salle.id === selectedSalleId) ||
      selectedFloor?.salles?.[0],
    [selectedFloor, selectedSalleId]
  );

  const selectedLayoutKey = `${selectedFloor?.id || DEFAULT_FLOOR_ID}:${
    selectedSalle?.id || DEFAULT_SALLE_ID
  }`;

  const salleTables = useMemo(() => {
    return tables
      .filter((table) => {
        const tableFloor = table.floor || DEFAULT_FLOOR_ID;
        const tableSalle = table.zone || DEFAULT_SALLE_ID;
        return tableFloor === selectedFloor?.id && tableSalle === selectedSalle?.id;
      })
      .map((table) => ({
        ...table,
        availability: availabilityMap[table._id]?.availability || "unknown",
        bookedReservation: availabilityMap[table._id]?.bookedReservation || null,
      }));
  }, [availabilityMap, selectedFloor, selectedSalle, tables]);

  const unplacedTableIdSet = useMemo(
    () => new Set(unplacedTableIds.map((id) => normalizeTableId(id)).filter(Boolean)),
    [unplacedTableIds]
  );

  const displayTables = useMemo(
    () =>
      salleTables.filter(
        (table) => !unplacedTableIdSet.has(normalizeTableId(table._id))
      ),
    [salleTables, unplacedTableIdSet]
  );

  const unplacedTables = useMemo(
    () =>
      salleTables.filter((table) =>
        unplacedTableIdSet.has(normalizeTableId(table._id))
      ),
    [salleTables, unplacedTableIdSet]
  );

  const availableTablesCount = displayTables.filter(
    (table) => table.availability === "available"
  ).length;

  const popupTable = useMemo(
    () => displayTables.find((table) => table._id === popupTableId) || null,
    [displayTables, popupTableId]
  );

  const filterTimeWindow = useMemo(
    () =>
      getReservationTimeWindow(null, {
        date: filters.date,
        time: filters.time,
        durationMinutes: filters.durationMinutes,
      }),
    [filters.date, filters.time, filters.durationMinutes]
  );

  const currentDispositions = useMemo(
    () => savedDispositions[selectedLayoutKey] || [],
    [savedDispositions, selectedLayoutKey]
  );

  const currentFolders = useMemo(
    () => planFolders[selectedLayoutKey] || [],
    [planFolders, selectedLayoutKey]
  );

  const resolveFloorSalleNames = (layoutKey = "", folder = null) => {
    const [floorId = "", salleId = ""] = String(layoutKey || "").split(":");
    const floorIndex = floors.findIndex((item) => item.id === floorId);
    const floor = floorIndex >= 0 ? floors[floorIndex] : null;
    const salleIndex = floor
      ? (floor.salles || []).findIndex((item) => item.id === salleId)
      : -1;
    const salle = salleIndex >= 0 ? floor.salles[salleIndex] : null;

    const folderFloorName = String(folder?.floorName || "").trim();
    const folderSalleName = String(folder?.salleName || "").trim();

    const floorName =
      (floor?.name && !looksLikeEntityId(floor.name, "floor") && floor.name) ||
      (folderFloorName &&
        !looksLikeEntityId(folderFloorName, "floor") &&
        folderFloorName) ||
      `${t("reservationFloor")} ${Math.max(1, floorIndex + 1)}`;

    const salleName =
      (salle?.name && !looksLikeEntityId(salle.name, "salle") && salle.name) ||
      (folderSalleName &&
        !looksLikeEntityId(folderSalleName, "salle") &&
        folderSalleName) ||
      `${t("reservationSalle")} ${Math.max(1, salleIndex + 1)}`;

    return {
      floorId: floor?.id || floorId || DEFAULT_FLOOR_ID,
      salleId: salle?.id || salleId || DEFAULT_SALLE_ID,
      floorName,
      salleName,
    };
  };

  const folderBrowseFloor =
    floors.find((floor) => floor.id === folderBrowseFloorId) || null;

  const folderBrowseSalles = folderBrowseFloor?.salles || [];

  const folderBrowseSalle =
    folderBrowseSalles.find((salle) => salle.id === folderBrowseSalleId) || null;

  const folderBrowseLayoutKey =
    folderBrowseFloor && folderBrowseSalle
      ? `${folderBrowseFloor.id}:${folderBrowseSalle.id}`
      : "";

  const folderBrowseLabels = useMemo(
    () =>
      folderBrowseLayoutKey
        ? resolveFloorSalleNames(folderBrowseLayoutKey)
        : { floorName: "", salleName: "" },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [folderBrowseLayoutKey, floors, t]
  );

  const folderBrowseFolders = useMemo(
    () =>
      folderBrowseLayoutKey
        ? planFolders[folderBrowseLayoutKey] || []
        : [],
    [planFolders, folderBrowseLayoutKey]
  );

  const activeFolder = useMemo(
    () => currentFolders.find((folder) => folder.id === activeFolderId) || null,
    [activeFolderId, currentFolders]
  );

  const folderNeedsFirstSave = useMemo(
    () =>
      Boolean(activeFolderId) &&
      !activeFolder?.tables?.length &&
      salleTables.length > 0,
    [activeFolder, activeFolderId, salleTables.length]
  );

  const folderSaveEnabled = useMemo(
    () =>
      Boolean(activeFolderId) &&
      (layoutDirty || folderNeedsFirstSave) &&
      salleTables.length > 0,
    [activeFolderId, folderNeedsFirstSave, layoutDirty, salleTables.length]
  );

  const canSaveLayout = activeFolderId
    ? folderSaveEnabled
    : layoutDirty;

  const defaultLayoutSnapshot = useMemo(() => {
    const store = readDefaultLayoutsStore();
    return store[selectedLayoutKey] || null;
  }, [selectedLayoutKey, defaultLayoutRevision]);

  const isOnDefaultLayout = Boolean(
    defaultLayoutSnapshot &&
      activeDispositionId === DEFAULT_LAYOUT_ACTIVE_ID &&
      !layoutDirty
  );

  const nextTableNumber = useMemo(() => {
    const maxNumber = tables.reduce(
      (max, table) => Math.max(max, Number(table.tableNumber) || 0),
      0
    );
    return maxNumber + 1;
  }, [tables]);

  const editTableMaxSeats = useMemo(() => {
    if (!editForm?.table) return 20;
    const table = tables.find((item) => item._id === editForm.table);
    return Math.max(1, Number(table?.seats) || 20);
  }, [editForm?.table, tables]);

  const editFormTimeWindow = useMemo(() => {
    if (!editForm) return null;
    return getReservationTimeWindow(editReservation, {
      date: editForm.date,
      time: editForm.time,
      durationMinutes: editForm.durationMinutes,
    });
  }, [editForm, editReservation]);

  const newReservationTableMaxSeats = useMemo(() => {
    if (!newReservationForm.table) return 20;
    const table = tables.find(
      (item) => String(item._id) === String(newReservationForm.table)
    );
    return Math.max(1, Number(table?.seats) || 20);
  }, [newReservationForm.table, tables]);

  const newReservationLockedTable = useMemo(() => {
    if (!newReservationLockedTableId) return null;
    return (
      tables.find((item) => String(item._id) === String(newReservationLockedTableId)) ||
      null
    );
  }, [newReservationLockedTableId, tables]);

  const getAssignableTableOptions = (reservation, windowOverrides = {}) => {
    const window =
      getReservationTimeWindow(reservation, windowOverrides) ||
      getReservationTimeWindow(reservation);
    const excludeId = getReservationId(reservation);
    const currentTableId = getReservationTableId(reservation);

    return tables
      .filter((table) => table.isActive !== false)
      .slice()
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
      .map((table) => {
        const occupied =
          window &&
          String(table._id) !== currentTableId &&
          isTableBookedAtTime(table._id, window, reservations, excludeId);
        return { table, occupied: Boolean(occupied) };
      });
  };

  const newReservationTableOptions = useMemo(
    () =>
      getAssignableTableOptions(
        { table: newReservationForm.table || null },
        {
          date: newReservationForm.date,
          time: newReservationForm.time,
          durationMinutes: newReservationForm.durationMinutes,
        }
      ),
    [newReservationForm, tables, reservations]
  );

  const reservationStats = useMemo(() => {
    const pending = reservations.filter((item) => item.status === "pending").length;
    const active = reservations.filter((item) =>
      ["confirmed", "arrived"].includes(item.status)
    ).length;
    const booked = displayTables.filter((table) => table.availability === "booked").length;
    return {
      pending,
      active,
      booked,
      total: reservations.length,
    };
  }, [displayTables, reservations]);

  const filteredReservations = useMemo(() => {
    let list = reservations;
    if (listStatusFilter) {
      list = list.filter((item) => item.status === listStatusFilter);
    }
    const query = listSearch.trim().toLowerCase();
    if (query) {
      list = list.filter((item) => {
        const haystack = [
          item.customerName,
          item.customerPhone,
          item.customerEmail,
          item.table?.name,
          item.tableNumber,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });
    }
    return list;
  }, [listSearch, listStatusFilter, reservations]);

  const listTotalPages = Math.max(
    1,
    Math.ceil(filteredReservations.length / RESERVATION_LIST_PAGE_SIZE)
  );
  const listCurrentPage = Math.min(listPage, listTotalPages);

  const pagedReservations = useMemo(() => {
    const start = (listCurrentPage - 1) * RESERVATION_LIST_PAGE_SIZE;
    return filteredReservations.slice(start, start + RESERVATION_LIST_PAGE_SIZE);
  }, [filteredReservations, listCurrentPage]);

  const listVisiblePages = useMemo(
    () => getReservationListVisiblePages(listCurrentPage, listTotalPages),
    [listCurrentPage, listTotalPages]
  );

  const listShowingFrom =
    filteredReservations.length === 0
      ? 0
      : (listCurrentPage - 1) * RESERVATION_LIST_PAGE_SIZE + 1;
  const listShowingTo = Math.min(
    listCurrentPage * RESERVATION_LIST_PAGE_SIZE,
    filteredReservations.length
  );

  useEffect(() => {
    setListPage(1);
  }, [listSearch, listStatusFilter, reservations.length]);

  useEffect(() => {
    document.body.classList.toggle(
      "reservation-plan-fullscreen-active",
      isPlanFullscreen
    );

    return () => {
      document.body.classList.remove("reservation-plan-fullscreen-active");
    };
  }, [isPlanFullscreen]);

  useEffect(() => {
    document.body.classList.toggle(TABLE_POPUP_BODY_CLASS, Boolean(popupTableId));
    return () => document.body.classList.remove(TABLE_POPUP_BODY_CLASS);
  }, [popupTableId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const restoData = await fetchRestoDataByUser();
        const color =
          restoData?.restaurant?.menuColors?.tableColor ||
          restoData?.menuColors?.tableColor;
        if (!cancelled && color) {
          setDefaultTableColor(normalizeTableHex(color));
        }
      } catch (error) {
        console.error("Failed to load restaurant table color:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isPlanFullscreen) return undefined;

    const handleEscapeFullscreen = (event) => {
      if (event.key === "Escape") {
        setIsPlanFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleEscapeFullscreen);
    return () => window.removeEventListener("keydown", handleEscapeFullscreen);
  }, [isPlanFullscreen]);

  const loadTables = async () => {
    const data = await fetchReservationTables();
    setTables(Array.isArray(data) ? data : []);
    setSelectedTableId((prev) => prev || (Array.isArray(data) ? data[0]?._id : null));
  };

  const loadAvailability = async () => {
    const data = await fetchReservationAvailability(filters);
    setAvailabilityTables(Array.isArray(data?.tables) ? data.tables : []);
  };

  const loadReservations = async () => {
    setLoadingReservations(true);
    try {
      const data = await fetchReservations({
        all: true,
      });
      setReservations(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationLoadError")));
    } finally {
      setLoadingReservations(false);
    }
  };

  useEffect(() => {
    try {
      const savedFloors = JSON.parse(
        localStorage.getItem(FLOOR_CONFIG_STORAGE_KEY) || "null"
      );
      const nextFloors = normalizeFloorsConfig(
        Array.isArray(savedFloors) && savedFloors.length > 0
          ? savedFloors
          : createDefaultFloors(),
        t("reservationFloor"),
        t("reservationSalle")
      );
      setFloors(nextFloors);
      setFloorsConfigReady(true);

      const savedSelection = JSON.parse(
        localStorage.getItem(FLOOR_SELECTION_STORAGE_KEY) || "null"
      );
      const nextFloor =
        nextFloors.find((floor) => floor.id === savedSelection?.floorId) ||
        nextFloors[0];
      const nextSalle =
        nextFloor?.salles?.find((salle) => salle.id === savedSelection?.salleId) ||
        nextFloor?.salles?.[0];
      if (nextFloor?.id) setSelectedFloorId(nextFloor.id);
      if (nextSalle?.id) setSelectedSalleId(nextSalle.id);
      // Plan floor setup filters stay empty until the user chooses

      const savedLayouts = JSON.parse(
        localStorage.getItem(FLOOR_LAYOUTS_STORAGE_KEY) || "{}"
      );
      const layout = savedLayouts?.[`${nextFloor?.id}:${nextSalle?.id}`];
      if (layout?.doorPosition) {
        setDoorPosition(layout.doorPosition);
      } else {
        const savedDoorPosition = JSON.parse(
          localStorage.getItem(DOOR_STORAGE_KEY) || "null"
        );
        if (
          savedDoorPosition &&
          Number.isFinite(savedDoorPosition.x) &&
          Number.isFinite(savedDoorPosition.y)
        ) {
          setDoorPosition(savedDoorPosition);
        }
      }
      const savedFloorElements = JSON.parse(
        localStorage.getItem(FLOOR_ELEMENTS_STORAGE_KEY) || "[]"
      );
      setFloorElements(
        normalizeFloorElements(layout?.floorElements || savedFloorElements)
      );
      const savedDispositionMap = JSON.parse(
        localStorage.getItem(FLOOR_DISPOSITIONS_STORAGE_KEY) || "{}"
      );
      const savedFolderMap = JSON.parse(
        localStorage.getItem(FLOOR_FOLDERS_STORAGE_KEY) || "{}"
      );
      const migratedDispositions = migrateLegacyDefaultLayouts(
        savedDispositionMap && typeof savedDispositionMap === "object"
          ? savedDispositionMap
          : {}
      );
      const split = splitFoldersFromDispositions(
        migratedDispositions,
        savedFolderMap && typeof savedFolderMap === "object" ? savedFolderMap : {}
      );
      setSavedDispositions(split.dispositions);
      setPlanFolders(split.folders);
      localStorage.setItem(
        FLOOR_DISPOSITIONS_STORAGE_KEY,
        JSON.stringify(split.dispositions)
      );
      localStorage.setItem(
        FLOOR_FOLDERS_STORAGE_KEY,
        JSON.stringify(split.folders)
      );
      setDefaultLayoutRevision((prev) => prev + 1);
      didLoadFloorConfig.current = true;
    } catch {
      const fallbackFloors = createDefaultFloors();
      setFloors(fallbackFloors);
      setFloorsConfigReady(true);
      setSelectedFloorId(DEFAULT_FLOOR_ID);
      setSelectedSalleId(DEFAULT_SALLE_ID);
      setFolderBrowseFloorId("");
      setFolderBrowseSalleId("");
      setDoorPosition(DEFAULT_DOOR_POSITION);
      setFloorElements([]);
      setSavedDispositions({});
      setPlanFolders({});
      didLoadFloorConfig.current = true;
    }
  }, [t]);

  useEffect(() => {
    setActiveDispositionId("");
    setUnplacedTableIds([]);
    if (!pendingFolderOpenRef.current) {
      setActiveFolderId("");
    }
  }, [selectedLayoutKey]);

  useEffect(() => {
    // Keep browse filters valid if floors/rooms are removed — never auto-select.
    if (!folderBrowseFloorId) return;

    const floorExists = floors.some((floor) => floor.id === folderBrowseFloorId);
    if (!floorExists) {
      setFolderBrowseFloorId("");
      setFolderBrowseSalleId("");
      return;
    }

    if (!folderBrowseSalleId) return;
    const floor = floors.find((item) => item.id === folderBrowseFloorId);
    const salleExists = (floor?.salles || []).some(
      (salle) => salle.id === folderBrowseSalleId
    );
    if (!salleExists) {
      setFolderBrowseSalleId("");
    }
  }, [floors, folderBrowseFloorId, folderBrowseSalleId]);

  useEffect(() => {
    if (!floorsConfigReady) return;
    persistFloorsConfig(floors);
    // persistFloorsConfig is stable enough for this write path
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floors, floorsConfigReady]);

  useEffect(() => {
    if (!didLoadFloorConfig.current) return;
    // Skip the first run after mount/hydration so we never overwrite storage with []
    if (!dispositionsReadyRef.current) {
      dispositionsReadyRef.current = true;
      return;
    }
    localStorage.setItem(
      FLOOR_DISPOSITIONS_STORAGE_KEY,
      JSON.stringify(savedDispositions)
    );
  }, [savedDispositions]);

  // NOTE: planFolders are written ONLY via commitFoldersStore / writeFolderSnapshot
  // (never from a useEffect) to avoid wiping saved table layouts on refresh.

  useEffect(() => {
    if (!didLoadFloorConfig.current) return;
    localStorage.setItem(
      FLOOR_SELECTION_STORAGE_KEY,
      JSON.stringify({ floorId: selectedFloor?.id, salleId: selectedSalle?.id })
    );

    try {
      const savedLayouts = JSON.parse(
        localStorage.getItem(FLOOR_LAYOUTS_STORAGE_KEY) || "{}"
      );
      const layout = savedLayouts?.[selectedLayoutKey];
      setDoorPosition(layout?.doorPosition || DEFAULT_DOOR_POSITION);
      setFloorElements(normalizeFloorElements(layout?.floorElements || []));
    } catch {
      setDoorPosition(DEFAULT_DOOR_POSITION);
      setFloorElements([]);
    }
  }, [selectedFloor, selectedLayoutKey, selectedSalle]);

  useEffect(() => {
    let isMounted = true;

    const loadTableLayout = async () => {
      setLoading(true);
      try {
        const data = await fetchReservationTables();
        if (!isMounted) return;
        setTables(Array.isArray(data) ? data : []);
        setSelectedTableId((prev) => prev || (Array.isArray(data) ? data[0]?._id : null));
      } catch (error) {
        toast.error(error?.message || t("reservationLoadError"));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadTableLayout();

    return () => {
      isMounted = false;
    };
  }, [t]);

  useEffect(() => {
    loadReservations();
  }, []);

  useEffect(() => {
    let active = true;
    const restaurantIdRef = { current: null };

    const joinRestaurantRoom = () => {
      const id = restaurantIdRef.current;
      if (id != null && id !== "") {
        socket.emit("join-restaurant", String(id));
      }
    };

    const onTableReservationChange = () => {
      if (!active) return;
      loadReservations();
      loadAvailability().catch(() => {});
    };

    const init = async () => {
      try {
        const user = await fetchConnectedUser();
        restaurantIdRef.current = user?.restaurantFK ?? null;
      } catch {
        restaurantIdRef.current = null;
      }
      if (!active) return;
      joinRestaurantRoom();
      socket.on("connect", joinRestaurantRoom);
      socket.on("new-table-reservation", onTableReservationChange);
    };

    init();

    return () => {
      active = false;
      socket.off("connect", joinRestaurantRoom);
      socket.off("new-table-reservation", onTableReservationChange);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadFilterData = async () => {
      try {
        const availabilityData = await fetchReservationAvailability(filters);
        if (!isMounted) return;
        setAvailabilityTables(
          Array.isArray(availabilityData?.tables) ? availabilityData.tables : []
        );
      } catch (error) {
        toast.error(error?.message || t("reservationLoadError"));
      }
    };

    loadFilterData();

    return () => {
      isMounted = false;
    };
  }, [filters, t]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const startDragSession = () => {
    if (dragListenersAttachedRef.current) return;
    window.addEventListener("pointermove", handleWindowPointerMoveRef.current);
    window.addEventListener("pointerup", handleWindowPointerUpRef.current);
    window.addEventListener("pointercancel", handleWindowPointerUpRef.current);
    dragListenersAttachedRef.current = true;
  };

  const endDragSession = () => {
    if (!dragListenersAttachedRef.current) return;
    window.removeEventListener("pointermove", handleWindowPointerMoveRef.current);
    window.removeEventListener("pointerup", handleWindowPointerUpRef.current);
    window.removeEventListener("pointercancel", handleWindowPointerUpRef.current);
    dragListenersAttachedRef.current = false;
  };

  const computeDragPosition = (event) => {
    const rect = floorRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;

    const nextX =
      dragState.current.startX +
      ((event.clientX - dragState.current.startClientX) / rect.width) * 100;
    const nextY =
      dragState.current.startY +
      ((event.clientY - dragState.current.startClientY) / rect.height) * 100;

    return { nextX, nextY };
  };

  const applyDragVisual = (position, size) => {
    const element = draggedElementRef.current;
    if (!element) return;
    if (position) {
      element.style.left = `${position.x}%`;
      element.style.top = `${position.y}%`;
    }
    if (size) {
      element.style.width = `${size.width * TABLE_RENDER_SCALE}px`;
      element.style.height = `${size.height * TABLE_RENDER_SCALE}px`;
    }
  };

  const commitDragPosition = () => {
    const state = dragState.current;
    if (!state?.moved) return;

    if (state.type === "resize") {
      if (!state.pendingSize) return;
      setLayoutDirty(true);
      setTables((prev) =>
        prev.map((table) =>
          String(table._id) === String(state.id)
            ? { ...table, size: state.pendingSize }
            : table
        )
      );
      if (String(popupTableId) === String(state.id)) {
        setPopupSize(state.pendingSize);
      }
      return;
    }

    if (!state.pendingPosition) return;

    setLayoutDirty(true);

    if (state.type === "door") {
      setDoorPosition(state.pendingPosition);
      return;
    }

    if (state.type === "floorElement") {
      setFloorElements((prev) =>
        prev.map((element) =>
          element.id === state.id
            ? { ...element, position: state.pendingPosition }
            : element
        )
      );
      return;
    }

    setTables((prev) =>
      prev.map((table) =>
        String(table._id) === String(state.id)
          ? { ...table, position: state.pendingPosition }
          : table
      )
    );
  };

  const handleWindowPointerMove = (event) => {
    if (!dragState.current) return;

    if (dragState.current.type === "resize") {
      const state = dragState.current;
      const rotationRad = ((Number(state.rotation) || 0) * Math.PI) / 180;
      const dx = event.clientX - state.startClientX;
      const dy = event.clientY - state.startClientY;
      const localDx = dx * Math.cos(rotationRad) + dy * Math.sin(rotationRad);
      const localDy = -dx * Math.sin(rotationRad) + dy * Math.cos(rotationRad);

      let nextWidth = state.startWidth;
      let nextHeight = state.startHeight;
      if (state.handle.x !== 0) {
        nextWidth =
          state.startWidth +
          (localDx * state.handle.x * 2) / TABLE_RENDER_SCALE;
      }
      if (state.handle.y !== 0) {
        nextHeight =
          state.startHeight +
          (localDy * state.handle.y * 2) / TABLE_RENDER_SCALE;
      }

      state.moved = true;
      didDragRef.current = true;
      state.pendingSize = {
        width: Math.round(clamp(nextWidth, MIN_TABLE_SIZE, MAX_TABLE_SIZE)),
        height: Math.round(clamp(nextHeight, MIN_TABLE_SIZE, MAX_TABLE_SIZE)),
      };
      applyDragVisual(null, state.pendingSize);
      return;
    }

    const coords = computeDragPosition(event);
    if (!coords) return;

    const { nextX, nextY } = coords;
    dragState.current.moved = true;
    didDragRef.current = true;

    if (dragState.current.type === "door") {
      dragState.current.pendingPosition = {
        x: clamp(nextX, 0, 90),
        y: clamp(nextY, 4, 94),
      };
    } else if (dragState.current.type === "floorElement") {
      dragState.current.pendingPosition = {
        x: clamp(nextX, 4, 96),
        y: clamp(nextY, 4, 96),
      };
    } else {
      dragState.current.pendingPosition = {
        x: clamp(nextX, 5, 95),
        y: clamp(nextY, 5, 95),
      };
    }

    applyDragVisual(dragState.current.pendingPosition);
  };

  const handleWindowPointerUp = () => {
    if (!dragState.current) return;
    commitDragPosition();
    endDragSession();
    dragState.current = null;
    draggedElementRef.current = null;
    didDragRef.current = false;
  };

  handleWindowPointerMoveRef.current = handleWindowPointerMove;
  handleWindowPointerUpRef.current = handleWindowPointerUp;

  useEffect(() => () => endDragSession(), []);

  const handlePointerDown = (event, table) => {
    if (event.button !== 0) return;
    if (isTableLockedForSlot(table)) return;
    const rect = floorRef.current?.getBoundingClientRect();
    if (!rect) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    didDragRef.current = false;
    draggedElementRef.current = event.currentTarget;
    dragState.current = {
      type: "table",
      id: table._id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: table.position?.x || 50,
      startY: table.position?.y || 50,
      moved: false,
      pendingPosition: null,
    };
    setSelectedTableId(table._id);
    startDragSession();
  };

  const handleDoorPointerDown = (event) => {
    if (event.button !== 0) return;
    const rect = floorRef.current?.getBoundingClientRect();
    if (!rect) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    didDragRef.current = false;
    draggedElementRef.current = event.currentTarget;
    dragState.current = {
      type: "door",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: doorPosition.x,
      startY: doorPosition.y,
      moved: false,
      pendingPosition: null,
    };
    startDragSession();
  };

  const handleFloorElementPointerDown = (event, element) => {
    if (event.button !== 0) return;
    const rect = floorRef.current?.getBoundingClientRect();
    if (!rect) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    didDragRef.current = false;
    draggedElementRef.current = event.currentTarget;
    dragState.current = {
      type: "floorElement",
      id: element.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: element.position?.x || 50,
      startY: element.position?.y || 50,
      moved: false,
      pendingPosition: null,
    };
    startDragSession();
  };

  const handleResizePointerDown = (event, table, handle) => {
    if (event.button !== 0) return;
    if (isTableLockedForSlot(table)) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    didDragRef.current = false;
    draggedElementRef.current = event.currentTarget.closest(".reservation-table");
    setSelectedTableId(table._id);
    dragState.current = {
      type: "resize",
      id: table._id,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startWidth: Number(table.size?.width) || 88,
      startHeight: Number(table.size?.height) || 88,
      rotation: Number(table.rotation) || 0,
      moved: false,
      pendingSize: null,
    };
    startDragSession();
  };

  const handleRotateTable = (event, tableId) => {
    event.stopPropagation();
    const table = displayTables.find((item) => item._id === tableId);
    if (isTableLockedForSlot(table)) {
      toast.warning(t("reservationTableLocked"));
      return;
    }
    setSelectedTableId(tableId);
    setLayoutDirty(true);
    setTables((prev) =>
      prev.map((table) => {
        if (table._id !== tableId) return table;
        const currentRotation = Number(table.rotation) || 0;
        const nextRotation = Math.abs(currentRotation % 180) === 90 ? 0 : 90;
        return { ...table, rotation: nextRotation };
      })
    );
  };

  const handleDeleteTable = async (event, tableId) => {
    event.stopPropagation();
    const table = displayTables.find((item) => item._id === tableId);
    if (isTableLockedForSlot(table)) {
      toast.warning(t("reservationTableDeleteBlocked"));
      return;
    }
    if (
      filterTimeWindow &&
      isTableBookedAtTime(tableId, filterTimeWindow, reservations)
    ) {
      toast.warning(t("reservationTableDeleteBlocked"));
      return;
    }
    setSubmittingTable(true);
    try {
      await deleteReservationTable(tableId);
      setTables((prev) => prev.filter((table) => table._id !== tableId));
      setAvailabilityTables((prev) => prev.filter((table) => table._id !== tableId));
      if (selectedTableId === tableId) {
        setSelectedTableId(null);
      }
      toast.success(t("reservationTableDeleted"));
    } catch (error) {
      toast.error(error?.message || t("reservationTableDeleteError"));
    } finally {
      setSubmittingTable(false);
    }
  };

  const handleQuickAddFloorElement = (option, index) => {
    setLayoutDirty(true);
    setFloorElements((prev) => [
      ...prev,
      {
        id: `${option.type}-${Date.now()}-${prev.length}`,
        type: option.type,
        position: {
          x: 18 + (index % 3) * 24,
          y: 64,
        },
        rotation: 0,
      },
    ]);
  };

  const handleRotateFloorElement = (event, elementId) => {
    event.stopPropagation();
    setLayoutDirty(true);
    setFloorElements((prev) =>
      prev.map((element) => {
        if (element.id !== elementId) return element;
        const currentRotation = Number(element.rotation) || 0;
        return { ...element, rotation: (currentRotation + 90) % 360 };
      })
    );
  };

  const handleDeleteFloorElement = (event, elementId) => {
    event.stopPropagation();
    setLayoutDirty(true);
    setFloorElements((prev) => prev.filter((element) => element.id !== elementId));
  };

  const handleOpenTablePopup = (table) => {
    setSelectedTableId(table._id);
    setPopupTableId(table._id);
    setPopupName(table.name || "");
    setPopupSeats(table.seats || 1);
    setPopupSize({
      width: table.size?.width || 88,
      height: table.size?.height || 88,
    });
    setPopupColor(table.color ? normalizeTableHex(table.color) : "");
  };

  const handleTablePointerUp = (table) => {
    const wasDragging = didDragRef.current;
    if (!wasDragging) {
      handleOpenTablePopup(table);
    }
  };

  const handleCloseTablePopup = () => {
    setPopupTableId(null);
  };

  const handleToggleReservationsList = () => {
    if (!showReservationsList) {
      loadReservations();
      if (!tables.length) {
        loadTables().catch(() => {});
      }
    }
    setShowReservationsList((prev) => !prev);
  };

  const handleNewReservationChange = (field, value) => {
    setNewReservationForm((prev) => ({ ...prev, [field]: value }));
    setNewReservationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateNewReservationForm = () => {
    const errors = {};
    const name = String(newReservationForm.customerName || "").trim();
    const phone = String(newReservationForm.customerPhone || "").trim();
    const email = String(newReservationForm.customerEmail || "").trim();
    const date = String(newReservationForm.date || "").trim();
    const time = String(newReservationForm.time || "").trim();
    const partySize = Number(newReservationForm.partySize);

    if (!name) {
      errors.customerName = t("reservationValidationNameRequired");
    }

    if (!phone) {
      errors.customerPhone = t("reservationValidationPhoneRequired");
    } else if (!isValidReservationPhone(phone)) {
      errors.customerPhone = t("reservationValidationPhoneInvalid");
    }

    if (email && !isValidReservationEmail(email)) {
      errors.customerEmail = t("reservationValidationEmailInvalid");
    }

    if (!date) {
      errors.date = t("reservationValidationDateRequired");
    }

    if (!time) {
      errors.time = t("reservationValidationTimeRequired");
    }

    if (!Number.isFinite(partySize) || partySize < 1) {
      errors.partySize = t("reservationValidationGuestsRequired");
    } else if (partySize > newReservationTableMaxSeats) {
      errors.partySize = t("reservationValidationGuestsTooMany", {
        max: newReservationTableMaxSeats,
      });
    }

    if (date && time) {
      const bookingWindow = getReservationTimeWindow(null, {
        date,
        time,
        durationMinutes: newReservationForm.durationMinutes,
      });
      if (!bookingWindow) {
        errors.date = errors.date || t("reservationValidationDateTimeInvalid");
        errors.time = errors.time || t("reservationValidationDateTimeInvalid");
      }
    }

    return errors;
  };

  const handleOpenNewReservationPopup = () => {
    if (!tables.length) {
      loadTables().catch(() => {});
    }
    setNewReservationLockedTableId("");
    setNewReservationErrors({});
    setShowNewReservationPopup(true);
  };

  const handleOpenReserveFromTable = () => {
    if (!popupTable || isTableLockedForSlot(popupTable)) return;
    if (!tables.length) {
      loadTables().catch(() => {});
    }
    const tableId = String(popupTable._id);
    const maxSeats = Math.max(1, Number(popupTable.seats) || 2);
    setNewReservationLockedTableId(tableId);
    setNewReservationErrors({});
    setNewReservationForm(
      createDefaultNewReservationForm({
        table: tableId,
        partySize: Math.min(2, maxSeats),
        date: filters.date || today(),
        time: filters.time || currentSystemTime(),
        durationMinutes: Number(filters.durationMinutes) || 90,
      })
    );
    setPopupTableId(null);
    setShowNewReservationPopup(true);
  };

  const handleCloseNewReservationPopup = () => {
    setShowNewReservationPopup(false);
    setNewReservationLockedTableId("");
    setNewReservationErrors({});
  };

  const handleResetNewReservationForm = () => {
    setNewReservationErrors({});
    if (newReservationLockedTableId) {
      const lockedTable =
        tables.find((item) => String(item._id) === String(newReservationLockedTableId)) ||
        null;
      const maxSeats = Math.max(1, Number(lockedTable?.seats) || 2);
      setNewReservationForm(
        createDefaultNewReservationForm({
          table: String(newReservationLockedTableId),
          partySize: Math.min(2, maxSeats),
          date: filters.date || today(),
          time: filters.time || currentSystemTime(),
          durationMinutes: Number(filters.durationMinutes) || 90,
        })
      );
      return;
    }
    setNewReservationForm(createDefaultNewReservationForm());
  };

  const handleSubmitNewReservation = async (event) => {
    event.preventDefault();

    const errors = validateNewReservationForm();
    setNewReservationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.warning(t("reservationValidationSummary"));
      return;
    }

    const assignedTableId = String(
      newReservationLockedTableId || newReservationForm.table || ""
    );

    const bookingWindow = getReservationTimeWindow(null, {
      date: newReservationForm.date,
      time: newReservationForm.time,
      durationMinutes: newReservationForm.durationMinutes,
    });

    if (
      assignedTableId &&
      bookingWindow &&
      isTableBookedAtTime(assignedTableId, bookingWindow, reservations)
    ) {
      toast.error(t("reservationTableUnavailable"));
      return;
    }

    setSubmittingNewReservation(true);
    try {
      await createReservation({
        ...(assignedTableId ? { table: assignedTableId } : {}),
        date: newReservationForm.date,
        time: newReservationForm.time,
        durationMinutes: Number(newReservationForm.durationMinutes),
        partySize: Number(newReservationForm.partySize),
        customerName: newReservationForm.customerName.trim(),
        customerPhone: newReservationForm.customerPhone.trim(),
        customerEmail: newReservationForm.customerEmail.trim(),
        clientNotes: newReservationForm.clientNotes.trim(),
        seatingPreference: newReservationForm.seatingPreference || "",
      });
      toast.success(t("reservationCreated"));
      setNewReservationErrors({});
      setNewReservationForm(createDefaultNewReservationForm());
      setNewReservationLockedTableId("");
      setShowNewReservationPopup(false);
      setPopupTableId(null);
      try {
        await Promise.all([loadAvailability(), loadReservations()]);
      } catch (reloadError) {
        console.error("Failed to reload after creating reservation:", reloadError);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationCreateError")));
    } finally {
      setSubmittingNewReservation(false);
    }
  };

  const applyReservationUpdate = (updated) => {
    if (!updated?._id) return { confirmationEmailSent: false };
    const { confirmationEmailSent, confirmationEmailError, ...reservationData } =
      updated;
    setReservations((prev) =>
      prev.map((item) => (item._id === reservationData._id ? reservationData : item))
    );
    setDetailsReservation((prev) =>
      prev && prev._id === reservationData._id ? reservationData : prev
    );
    setEditReservation((prev) =>
      prev && prev._id === reservationData._id ? reservationData : prev
    );
    return {
      confirmationEmailSent: Boolean(confirmationEmailSent),
      confirmationEmailError: confirmationEmailError || null,
    };
  };

  const handleReservationFieldUpdate = async (reservation, patch, confirmMeta = {}) => {
    if (!reservation?._id) return false;

    const escapeHtml = (value) =>
      String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const isStatusUpdate = patch.status !== undefined;
    const isTableUpdate = Object.prototype.hasOwnProperty.call(patch, "table");
    const confirmingNow =
      isStatusUpdate &&
      patch.status === "confirmed" &&
      reservation.status !== "confirmed";
    const guestName = escapeHtml(
      reservation.customerName || t("reservationCustomer")
    );

    if (isTableUpdate && patch.table) {
      const window = getReservationTimeWindow(reservation);
      const nextStatus = patch.status || reservation.status;
      if (
        window &&
        ACTIVE_RESERVATION_STATUSES.includes(nextStatus) &&
        isTableBookedAtTime(patch.table, window, reservations, reservation._id)
      ) {
        toast.error(t("reservationTableUnavailable"));
        return false;
      }
    }

    let confirmTitle = t("reservationUpdateConfirmTitle");
    let confirmHtml = t("reservationUpdateConfirmText");

    if (isStatusUpdate) {
      confirmTitle = t("reservationStatusChangeConfirmTitle");
      confirmHtml = t("reservationStatusChangeConfirmText", {
        name: guestName,
        from: t(`reservationStatus_${reservation.status}`),
        to: t(`reservationStatus_${patch.status}`),
      });
      if (confirmingNow && reservation.customerEmail) {
        confirmHtml = `${confirmHtml}<br/><br/><span style="color:#059669;font-weight:600;">${t(
          "reservationStatusConfirmEmailHint"
        )}</span>`;
      } else if (confirmingNow && !reservation.customerEmail) {
        confirmHtml = `${confirmHtml}<br/><br/><span style="color:#d97706;">${t(
          "reservationConfirmedNoEmail"
        )}</span>`;
      }
    } else if (isTableUpdate) {
      confirmTitle = t("reservationTableChangeConfirmTitle");
      confirmHtml = t("reservationTableChangeConfirmText", {
        name: guestName,
        table: escapeHtml(
          confirmMeta.tableLabel || t("reservationTableUnassigned")
        ),
      });
    }

    const result = await Swal.fire({
      title: confirmTitle,
      html: confirmHtml,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("reservationUpdateConfirmAction"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#fa8072",
      cancelButtonColor: "#94a3b8",
    });

    if (!result.isConfirmed) return false;

    setUpdatingReservationId(reservation._id);
    try {
      const updated = await updateReservation(reservation._id, patch);
      const meta = applyReservationUpdate(updated);
      if (isTableUpdate || isStatusUpdate) {
        await loadAvailability().catch(() => {});
      }
      if (confirmingNow) {
        if (updated?.confirmationEmailSent || meta?.confirmationEmailSent) {
          toast.success(t("reservationConfirmedEmailSent"));
        } else if (reservation.customerEmail) {
          toast.success(t("reservationUpdated"));
          toast.warning(t("reservationConfirmedEmailFailed"));
        } else {
          toast.success(t("reservationUpdated"));
          toast.info(t("reservationConfirmedNoEmail"));
        }
      } else {
        toast.success(t("reservationUpdated"));
      }
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationUpdateError")));
      return false;
    } finally {
      setUpdatingReservationId(null);
    }
  };

  const handleOpenEditReservation = (reservation) => {
    if (!reservation?._id) return;
    setDetailsReservation(null);
    setEditReservation(reservation);
    setEditForm(reservationToEditForm(reservation));
    if (!tables.length) {
      loadTables().catch(() => {});
    }
  };

  const handleCloseEditReservation = () => {
    setEditReservation(null);
    setEditForm(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSubmitEditReservation = async (event) => {
    event.preventDefault();
    if (!editReservation?._id || !editForm) return;

    if (!editForm.customerName.trim() || !editForm.customerPhone.trim()) {
      toast.warning(t("reservationCustomerRequired"));
      return;
    }

    if (
      editForm.table &&
      ACTIVE_RESERVATION_STATUSES.includes(editForm.status)
    ) {
      const effectiveDuration = editForm.actualDurationMinutes
        ? Number(editForm.actualDurationMinutes)
        : Number(editForm.durationMinutes);
      const bookingWindow = getReservationTimeWindow(editReservation, {
        date: editForm.date,
        time: editForm.time,
        durationMinutes: effectiveDuration,
      });
      if (
        bookingWindow &&
        isTableBookedAtTime(
          editForm.table,
          bookingWindow,
          reservations,
          editReservation._id
        )
      ) {
        toast.error(t("reservationTableUnavailable"));
        return;
      }
    }

    if (
      editForm.actualDurationMinutes &&
      (Number(editForm.actualDurationMinutes) < 15 ||
        !Number.isFinite(Number(editForm.actualDurationMinutes)))
    ) {
      toast.warning(t("reservationActualDurationInvalid"));
      return;
    }

    const escapeHtml = (value) =>
      String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const guestName = escapeHtml(
      editForm.customerName.trim() || t("reservationCustomer")
    );
    const confirmingNow =
      editForm.status === "confirmed" && editReservation.status !== "confirmed";
    const statusChanged = editForm.status !== editReservation.status;

    let confirmHtml = t("reservationEditConfirmText", { name: guestName });
    if (confirmingNow && editForm.customerEmail.trim()) {
      confirmHtml = `${confirmHtml}<br/><br/><span style="color:#059669;font-weight:600;">${t(
        "reservationStatusConfirmEmailHint"
      )}</span>`;
    } else if (confirmingNow && !editForm.customerEmail.trim()) {
      confirmHtml = `${confirmHtml}<br/><br/><span style="color:#d97706;">${t(
        "reservationConfirmedNoEmail"
      )}</span>`;
    } else if (statusChanged) {
      confirmHtml = `${confirmHtml}<br/><br/>${t("reservationStatusChangeConfirmText", {
        name: guestName,
        from: t(`reservationStatus_${editReservation.status}`),
        to: t(`reservationStatus_${editForm.status}`),
      })}`;
    }

    const result = await Swal.fire({
      title: t("reservationEditConfirmTitle"),
      html: confirmHtml,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("reservationSaveChanges"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#fa8072",
      cancelButtonColor: "#94a3b8",
      heightAuto: false,
      customClass: {
        container: "reservation-swal-on-top",
      },
    });

    if (!result.isConfirmed) return;

    setSubmittingEdit(true);
    try {
      const updatePayload = {
        customerName: editForm.customerName.trim(),
        customerPhone: editForm.customerPhone.trim(),
        customerEmail: editForm.customerEmail.trim(),
        preferredContact: editForm.preferredContact,
        date: editForm.date,
        time: editForm.time,
        durationMinutes: Number(editForm.durationMinutes),
        partySize: Number(editForm.partySize),
        table: editForm.table || null,
        status: editForm.status,
        clientNotes: editForm.clientNotes.trim(),
      };

      if (editForm.seatingPreference) {
        updatePayload.seatingPreference = editForm.seatingPreference;
      } else {
        updatePayload.seatingPreference = "";
      }

      if (editForm.actualDurationMinutes) {
        updatePayload.actualDurationMinutes = Number(editForm.actualDurationMinutes);
      }

      const updated = await updateReservation(editReservation._id, updatePayload);
      const meta = applyReservationUpdate(updated);
      handleCloseEditReservation();
      setDetailsReservation(null);

      const email = editForm.customerEmail.trim();
      if (confirmingNow) {
        if (updated?.confirmationEmailSent || meta?.confirmationEmailSent) {
          toast.success(t("reservationConfirmedEmailSent"));
        } else if (email) {
          toast.success(t("reservationUpdated"));
          toast.warning(t("reservationConfirmedEmailFailed"));
        } else {
          toast.success(t("reservationUpdated"));
          toast.info(t("reservationConfirmedNoEmail"));
        }
      } else {
        toast.success(t("reservationUpdated"));
      }

      try {
        await loadAvailability();
        if (showReservationsList) {
          await loadReservations();
        }
      } catch (reloadError) {
        console.error("Failed to reload after updating reservation:", reloadError);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationUpdateError")));
    } finally {
      setSubmittingEdit(false);
    }
  };

  const handleArchiveReservation = async (reservation) => {
    if (!reservation?._id) return;

    const result = await Swal.fire({
      title: t("reservationArchiveConfirm"),
      text: t("reservationArchiveConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("reservationArchiveAction"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#fa8072",
      cancelButtonColor: "#94a3b8",
    });

    if (!result.isConfirmed) return;

    setArchivingReservationId(reservation._id);
    try {
      await updateReservationArchive(reservation._id, true);
      setReservations((prev) =>
        prev.filter((item) => item._id !== reservation._id)
      );
      if (detailsReservation?._id === reservation._id) {
        setDetailsReservation(null);
      }
      if (editReservation?._id === reservation._id) {
        handleCloseEditReservation();
      }
      await loadAvailability();
      toast.success(t("reservationArchived"));
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationUpdateError")));
    } finally {
      setArchivingReservationId(null);
    }
  };

  const handleSelectFloor = (floorId) => {
    const floor = floors.find((item) => item.id === floorId);
    if (!floor) return;
    setSelectedFloorId(floor.id);
    setSelectedSalleId(floor.salles?.[0]?.id || DEFAULT_SALLE_ID);
  };

  const handleStepFloor = (direction) => {
    if (floors.length === 0) return;
    const currentIndex = Math.max(
      0,
      floors.findIndex((floor) => floor.id === selectedFloor?.id)
    );
    const nextIndex = (currentIndex + direction + floors.length) % floors.length;
    handleSelectFloor(floors[nextIndex].id);
  };

  const handleStepSalle = (direction) => {
    const salles = selectedFloor?.salles || [];
    if (salles.length === 0) return;
    const currentIndex = Math.max(
      0,
      salles.findIndex((salle) => salle.id === selectedSalle?.id)
    );
    const nextIndex = (currentIndex + direction + salles.length) % salles.length;
    setSelectedSalleId(salles[nextIndex].id);
  };

  const persistFloorsConfig = (nextFloors) => {
    const normalized = normalizeFloorsConfig(
      nextFloors,
      t("reservationFloor"),
      t("reservationSalle")
    );
    try {
      localStorage.setItem(FLOOR_CONFIG_STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.error("Failed to persist floors config:", error);
    }
    return normalized;
  };

  const handleAddFloor = () => {
    const floorId = `floor-${Date.now()}`;
    const salleId = `salle-${Date.now() + 1}`;
    const floorName =
      newFloorName.trim() || `${t("reservationFloor")} ${floors.length + 1}`;
    const nextFloor = {
      id: floorId,
      name: floorName,
      salles: [{ id: salleId, name: `${t("reservationSalle")} 1` }],
    };
    setFloors((prev) => {
      const next = [...prev, nextFloor];
      persistFloorsConfig(next);
      return next;
    });
    setSelectedFloorId(floorId);
    setSelectedSalleId(salleId);
    setNewFloorName("");
  };

  const handleAddSalle = () => {
    if (!selectedFloor) return;
    const salleId = `salle-${Date.now()}`;
    const salleName =
      newSalleName.trim() ||
      `${t("reservationSalle")} ${(selectedFloor.salles?.length || 0) + 1}`;
    setFloors((prev) => {
      const next = prev.map((floor) =>
        floor.id === selectedFloor.id
          ? {
              ...floor,
              salles: [...(floor.salles || []), { id: salleId, name: salleName }],
            }
          : floor
      );
      persistFloorsConfig(next);
      return next;
    });
    setSelectedSalleId(salleId);
    setNewSalleName("");
  };

  const removeLayoutKeysMatching = (shouldRemove) => {
    const stripMap = (raw) => {
      const next = { ...(raw || {}) };
      Object.keys(next).forEach((key) => {
        if (shouldRemove(key)) delete next[key];
      });
      return next;
    };

    const readMap = (storageKey) => {
      try {
        const parsed = JSON.parse(localStorage.getItem(storageKey) || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    };

    const nextLayouts = stripMap(readMap(FLOOR_LAYOUTS_STORAGE_KEY));
    const nextDispositions = stripMap(readMap(FLOOR_DISPOSITIONS_STORAGE_KEY));
    const nextFolders = stripMap(readMap(FLOOR_FOLDERS_STORAGE_KEY));
    const nextDefaults = stripMap(readMap(FLOOR_DEFAULT_LAYOUTS_STORAGE_KEY));

    localStorage.setItem(FLOOR_LAYOUTS_STORAGE_KEY, JSON.stringify(nextLayouts));
    localStorage.setItem(
      FLOOR_DISPOSITIONS_STORAGE_KEY,
      JSON.stringify(nextDispositions)
    );
    localStorage.setItem(FLOOR_FOLDERS_STORAGE_KEY, JSON.stringify(nextFolders));
    writeDefaultLayoutsStore(nextDefaults);

    setSavedDispositions(nextDispositions);
    setPlanFolders(nextFolders);
  };

  const handleDeleteFloor = async (event, floor) => {
    event.stopPropagation();
    if (!floor?.id) return;

    if (floors.length <= 1) {
      toast.warning(t("reservationCannotDeleteLastFloor"));
      return;
    }

    const result = await Swal.fire({
      title: t("areYouSure") || "Are you sure?",
      text: t("reservationDeleteFloorConfirm", { name: floor.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("reservationDeleteFloor"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      heightAuto: false,
      customClass: { container: "reservation-swal-on-top" },
    });
    if (!result.isConfirmed) return;

    const wasSelected = selectedFloorId === floor.id;
    const nextFloors = floors.filter((item) => item.id !== floor.id);
    persistFloorsConfig(nextFloors);
    setFloors(nextFloors);
    removeLayoutKeysMatching((key) => String(key).startsWith(`${floor.id}:`));

    if (wasSelected) {
      const fallback = nextFloors[0];
      setSelectedFloorId(fallback?.id || DEFAULT_FLOOR_ID);
      setSelectedSalleId(fallback?.salles?.[0]?.id || DEFAULT_SALLE_ID);
      setActiveFolderId("");
      setUnplacedTableIds([]);
    }

    toast.success(t("reservationFloorDeleted", { name: floor.name }));
  };

  const handleDeleteSalle = async (event, salle) => {
    event.stopPropagation();
    if (!selectedFloor?.id || !salle?.id) return;

    const salles = selectedFloor.salles || [];
    if (salles.length <= 1) {
      toast.warning(t("reservationCannotDeleteLastSalle"));
      return;
    }

    const result = await Swal.fire({
      title: t("areYouSure") || "Are you sure?",
      text: t("reservationDeleteSalleConfirm", { name: salle.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("reservationDeleteSalle"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      heightAuto: false,
      customClass: { container: "reservation-swal-on-top" },
    });
    if (!result.isConfirmed) return;

    const wasSelected = selectedSalleId === salle.id;
    const nextSalles = salles.filter((item) => item.id !== salle.id);
    const nextFloors = floors.map((floor) =>
      floor.id === selectedFloor.id ? { ...floor, salles: nextSalles } : floor
    );
    persistFloorsConfig(nextFloors);
    setFloors(nextFloors);
    removeLayoutKeysMatching(
      (key) => String(key) === `${selectedFloor.id}:${salle.id}`
    );

    if (wasSelected) {
      setSelectedSalleId(nextSalles[0]?.id || DEFAULT_SALLE_ID);
      setActiveFolderId("");
      setUnplacedTableIds([]);
    }

    toast.success(t("reservationSalleDeleted", { name: salle.name }));
  };

  const persistDispositions = (nextDispositions) => {
    const normalizedMap = Object.fromEntries(
      Object.entries(nextDispositions || {}).map(([key, list]) => [
        key,
        normalizeDispositionList(list),
      ])
    );
    dispositionsReadyRef.current = true;
    setSavedDispositions(normalizedMap);
    localStorage.setItem(
      FLOOR_DISPOSITIONS_STORAGE_KEY,
      JSON.stringify(normalizedMap)
    );
  };

  const normalizeFoldersMap = (nextFolders) =>
    Object.fromEntries(
      Object.entries(nextFolders || {}).map(([key, list]) => [
        key,
        normalizeFolderList(list),
      ])
    );

  const readFoldersStore = () => {
    try {
      const parsed = JSON.parse(
        localStorage.getItem(FLOOR_FOLDERS_STORAGE_KEY) || "{}"
      );
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  };

  /** Single write path for folders — keeps React state and localStorage in sync. */
  const commitFoldersStore = (nextFolders) => {
    const normalizedMap = normalizeFoldersMap(nextFolders);
    localStorage.setItem(FLOOR_FOLDERS_STORAGE_KEY, JSON.stringify(normalizedMap));
    foldersReadyRef.current = true;
    setPlanFolders(normalizedMap);
    return normalizedMap;
  };

  const persistFolders = (nextFolders) => commitFoldersStore(nextFolders);

  const getSalleTablesSnapshot = (tableList = tables) =>
    (Array.isArray(tableList) ? tableList : [])
      .filter((table) => {
        const tableFloor = table.floor || DEFAULT_FLOOR_ID;
        const tableSalle = table.zone || DEFAULT_SALLE_ID;
        return (
          tableFloor === (selectedFloor?.id || DEFAULT_FLOOR_ID) &&
          tableSalle === (selectedSalle?.id || DEFAULT_SALLE_ID)
        );
      })
      .map((table) => ({
        tableId: normalizeTableId(table._id),
        position: {
          x: Number(table.position?.x ?? 50),
          y: Number(table.position?.y ?? 50),
        },
        size: {
          width: Number(table.size?.width ?? 88),
          height: Number(table.size?.height ?? 88),
        },
        rotation: Number(table.rotation) || 0,
      }));

  const findFolderInStore = (folderId, store = readFoldersStore()) => {
    if (!folderId) return null;
    const preferred = (store[selectedLayoutKey] || []).find(
      (item) => item.id === folderId
    );
    if (preferred) return { layoutKey: selectedLayoutKey, folder: preferred };

    for (const [layoutKey, list] of Object.entries(store)) {
      const folder = (Array.isArray(list) ? list : []).find(
        (item) => item.id === folderId
      );
      if (folder) return { layoutKey, folder };
    }
    return null;
  };

  const persistDefaultLayout = (snapshot) => {
    const store = readDefaultLayoutsStore();
    writeDefaultLayoutsStore({
      ...store,
      [selectedLayoutKey]: snapshot,
    });
    setDefaultLayoutRevision((prev) => prev + 1);
  };

  const buildVersionSnapshot = ({
    id = `disposition-${Date.now()}`,
    name,
  }) => ({
    id,
    name,
    createdAt: new Date().toISOString(),
    tables: displayTables.map((table) => ({
      tableId: normalizeTableId(table._id),
      position: {
        x: Number(table.position?.x ?? 50),
        y: Number(table.position?.y ?? 50),
      },
      size: {
        width: Number(table.size?.width ?? 88),
        height: Number(table.size?.height ?? 88),
      },
      rotation: Number(table.rotation) || 0,
    })),
    doorPosition,
    floorElements,
  });

  // ── Versions: save slots when updating the current plan ─────────
  const saveCurrentDisposition = ({ showToast = true } = {}) => {
    if (displayTables.length === 0) {
      if (showToast) toast.warning(t("reservationDispositionNoTables"));
      return false;
    }
    if (currentDispositions.length >= MAX_DISPOSITIONS_PER_SALLE) {
      if (showToast) toast.warning(t("reservationDispositionLimit"));
      return false;
    }

    const disposition = buildVersionSnapshot({
      name: `${t("reservationDisposition")} ${currentDispositions.length + 1}`,
    });
    persistDispositions({
      ...savedDispositions,
      [selectedLayoutKey]: [...currentDispositions, disposition],
    });
    setActiveDispositionId(disposition.id);
    if (showToast) toast.success(t("reservationDispositionSaved"));
    return true;
  };

  const handleSaveDisposition = () => {
    saveCurrentDisposition();
  };

  const updateDispositionSnapshot = (dispositionId, { showToast = false } = {}) => {
    if (!dispositionId || displayTables.length === 0) return false;
    const target = currentDispositions.find((item) => item.id === dispositionId);
    if (!target) return false;

    const updated = buildVersionSnapshot({
      id: target.id,
      name: target.name,
    });
    persistDispositions({
      ...savedDispositions,
      [selectedLayoutKey]: currentDispositions.map((item) =>
        item.id === dispositionId ? updated : item
      ),
    });
    setActiveDispositionId(updated.id);
    if (showToast) toast.success(t("reservationDispositionSaved"));
    return true;
  };

  const handleDeleteDisposition = (event, dispositionId) => {
    event.stopPropagation();
    persistDispositions({
      ...savedDispositions,
      [selectedLayoutKey]: currentDispositions.filter(
        (item) => item.id !== dispositionId
      ),
    });
    if (activeDispositionId === dispositionId) setActiveDispositionId("");
    toast.success(t("reservationDispositionDeleted"));
  };

  const handleApplyDisposition = async (disposition) => {
    if (!disposition) return;
    if (activeFolderId) safeguardActiveFolder();
    setActiveFolderId("");
    setUnplacedTableIds([]);
    await applyLayoutSnapshot(disposition, {
      isDefaultApply: false,
      dispositionId: disposition.id,
      folderId: "",
    });
  };

  // ── Folders: named containers that hold one version to reuse ────
  const promptFolderName = async ({
    title,
    confirmText,
    defaultName = "",
  } = {}) => {
    const result = await Swal.fire({
      title: title || t("reservationFolderCreateTitle"),
      input: "text",
      inputLabel: t("reservationFolderNameLabel"),
      inputValue: defaultName,
      inputPlaceholder: t("reservationFolderNamePlaceholder"),
      showCancelButton: true,
      confirmButtonText: confirmText || t("reservationFolderCreateAction"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#fa8072",
      cancelButtonColor: "#94a3b8",
      heightAuto: false,
      customClass: { container: "reservation-swal-on-top" },
      inputValidator: (value) => {
        const trimmed = String(value || "").trim();
        if (!trimmed) return t("reservationFolderNameRequired");
        if (trimmed.length > 40) return t("reservationFolderNameTooLong");
        return null;
      },
    });
    if (!result.isConfirmed) return null;
    return String(result.value || "").trim();
  };

  const findFolderByName = (name, excludeId = "", layoutKey = folderBrowseLayoutKey) => {
    const normalized = String(name || "").trim().toLowerCase();
    if (!normalized) return null;
    const list = planFolders[layoutKey] || [];
    return (
      list.find(
        (folder) =>
          folder.id !== excludeId &&
          String(folder.name || "").trim().toLowerCase() === normalized
      ) || null
    );
  };

  const buildFolderVersion = (
    folder,
    tableEntries = [],
    extras = {}
  ) => {
    const labels = resolveFloorSalleNames(
      `${extras.floorId || folder.floorId || selectedFloor?.id || DEFAULT_FLOOR_ID}:${
        extras.salleId || folder.salleId || selectedSalle?.id || DEFAULT_SALLE_ID
      }`,
      folder
    );
    return {
      id: folder.id,
      name: folder.name,
      isFolder: true,
      floorId: labels.floorId,
      salleId: labels.salleId,
      floorName:
        (extras.floorName &&
          !looksLikeEntityId(extras.floorName, "floor") &&
          extras.floorName) ||
        labels.floorName,
      salleName:
        (extras.salleName &&
          !looksLikeEntityId(extras.salleName, "salle") &&
          extras.salleName) ||
        labels.salleName,
      createdAt: folder.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tables: Array.isArray(tableEntries) ? tableEntries : [],
      doorPosition: extras.doorPosition ?? doorPosition ?? DEFAULT_DOOR_POSITION,
      floorElements: normalizeFloorElements(
        extras.floorElements ?? floorElements ?? []
      ),
    };
  };

  /**
   * Synchronously write the current plan into a folder in localStorage + state.
   * This is what makes the saved version survive refresh.
   */
  const writeFolderSnapshot = (
    folderId,
    {
      tableEntries = getSalleTablesSnapshot(tables),
      door = doorPosition,
      elements = floorElements,
      folderName = "",
    } = {}
  ) => {
    if (!folderId) return null;

    const store = readFoldersStore();
    const existing = findFolderInStore(folderId, store);
    const layoutKey = existing?.layoutKey || selectedLayoutKey;
    const list = Array.isArray(store[layoutKey]) ? [...store[layoutKey]] : [];
    const index = list.findIndex((item) => item.id === folderId);

    const baseFolder =
      index >= 0
        ? list[index]
        : {
            id: folderId,
            name:
              folderName ||
              activeFolder?.name ||
              existing?.folder?.name ||
              t("reservationFolder"),
            createdAt: new Date().toISOString(),
          };

    const updated = buildFolderVersion(baseFolder, tableEntries, {
      doorPosition: door,
      floorElements: elements,
    });

    if (index >= 0) list[index] = updated;
    else list.push(updated);

    const nextStore = { ...store, [layoutKey]: list };
    commitFoldersStore(nextStore);
    return updated;
  };

  const safeguardActiveFolder = ({
    tableEntries = getSalleTablesSnapshot(tables),
    showToast = false,
  } = {}) => {
    if (!activeFolderId) return null;
    const saved = writeFolderSnapshot(activeFolderId, { tableEntries });
    if (saved && showToast) {
      toast.success(
        t("reservationFolderVersionSaved", {
          name: saved.name || t("reservationFolder"),
        })
      );
    }
    return saved;
  };

  const handleCreateFolder = async () => {
    if (!folderBrowseFloor || !folderBrowseSalle) {
      toast.warning(t("reservationFoldersPickFloorSalle"));
      return;
    }
    if (folderBrowseFolders.length >= MAX_FOLDERS_PER_SALLE) {
      toast.warning(t("reservationFolderLimit"));
      return;
    }

    const name = await promptFolderName();
    if (!name) return;
    if (findFolderByName(name, "", folderBrowseLayoutKey)) {
      toast.warning(t("reservationFolderNameExists", { name }));
      return;
    }

    const labels = resolveFloorSalleNames(folderBrowseLayoutKey);
    const folder = buildFolderVersion(
      {
        id: `folder-${Date.now()}`,
        name,
        createdAt: new Date().toISOString(),
        floorId: labels.floorId,
        salleId: labels.salleId,
        floorName: labels.floorName,
        salleName: labels.salleName,
      },
      [],
      {
        doorPosition: DEFAULT_DOOR_POSITION,
        floorElements: [],
        floorId: labels.floorId,
        salleId: labels.salleId,
        floorName: labels.floorName,
        salleName: labels.salleName,
      }
    );

    const store = readFoldersStore();
    commitFoldersStore({
      ...store,
      [folderBrowseLayoutKey]: [
        ...(store[folderBrowseLayoutKey] || []),
        folder,
      ],
    });

    setUnplacedTableIds([]);
    setLayoutDirty(false);

    // Open the new empty folder so adding tables + Save folder stores this plan
    if (folderBrowseLayoutKey !== selectedLayoutKey) {
      pendingFolderOpenRef.current = folder.id;
      setSelectedFloorId(folderBrowseFloor.id);
      setSelectedSalleId(folderBrowseSalle.id);
    } else {
      setActiveFolderId(folder.id);
      setActiveDispositionId("");
    }

    toast.success(
      t("reservationFolderCreatedReady", {
        name,
        floor: labels.floorName,
        salle: labels.salleName,
      })
    );
    void logAdminHistoryAction({
      action: "plan_floor_setup.create",
      description: `Created plan floor setup "${name}" (${labels.floorName} / ${labels.salleName})`,
      metadata: {
        folderId: folder.id,
        name,
        floorName: labels.floorName,
        salleName: labels.salleName,
        layoutKey: folderBrowseLayoutKey,
      },
    });
    requestAnimationFrame(() => {
      floorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    });
  };

  const handleSelectFolderBrowseFloor = (floorId) => {
    const floor = floors.find((item) => item.id === floorId);
    if (!floor) {
      setFolderBrowseFloorId("");
      setFolderBrowseSalleId("");
      return;
    }
    setFolderBrowseFloorId(floor.id);
    // Wait for an explicit room choice before showing setups
    setFolderBrowseSalleId("");
  };

  const handleSelectFolderBrowseSalle = (salleId) => {
    setFolderBrowseSalleId(salleId || "");
  };

  /**
   * Assign (save) the current floor plan design into a setup.
   * Without a target: open stepped picker (Floor → Room → Setup).
   * With a target (row action): write immediately.
   */
  const applyDesignToSetup = (folder, layoutKey = selectedLayoutKey) => {
    if (!folder?.id) {
      toast.warning(t("reservationAffectPickRequired"));
      return false;
    }

    const tableEntries = getSalleTablesSnapshot(tables);
    if (!tableEntries.length && floorElements.length === 0) {
      toast.warning(t("reservationAffectNoDesign"));
      return false;
    }

    const located = findFolderInStore(folder.id);
    const folderLayoutKey =
      located?.layoutKey || folder.layoutKey || layoutKey;

    if (folderLayoutKey !== selectedLayoutKey) {
      const labels = resolveFloorSalleNames(folderLayoutKey, folder);
      toast.warning(
        t("reservationAffectWrongRoom", {
          floor: labels.floorName,
          salle: labels.salleName,
        })
      );
      return false;
    }

    const saved = writeFolderSnapshot(folder.id, {
      tableEntries,
      door: doorPosition,
      elements: floorElements,
      folderName: folder.name || "",
    });

    if (!saved) {
      toast.error(t("reservationAffectFailed"));
      return false;
    }

    const labels = resolveFloorSalleNames(selectedLayoutKey, saved);
    setActiveFolderId(saved.id);
    setActiveDispositionId("");
    setUnplacedTableIds([]);
    setLayoutDirty(false);
    toast.success(
      t("reservationAffectSuccess", {
        name: saved.name || t("reservationFolder"),
        floor: labels.floorName,
        salle: labels.salleName,
      })
    );
    void logAdminHistoryAction({
      action: "plan_floor_setup.update",
      description: `Updated plan floor setup "${saved.name || folder.name || "setup"}" (${labels.floorName} / ${labels.salleName})`,
      metadata: {
        folderId: saved.id,
        name: saved.name || folder.name || "",
        floorName: labels.floorName,
        salleName: labels.salleName,
        layoutKey: selectedLayoutKey,
      },
    });
    return true;
  };

  const handleAffectDesignToSetup = (targetFolder = null) => {
    if (savingLayout) return;

    const tableEntries = getSalleTablesSnapshot(tables);
    if (!tableEntries.length && floorElements.length === 0) {
      toast.warning(t("reservationAffectNoDesign"));
      return;
    }

    if (targetFolder) {
      applyDesignToSetup(targetFolder);
      return;
    }

    // If a setup is already open, offer: save current OR choose another
    if (activeFolder) {
      setAffectPicker({
        step: 0,
        floorId: selectedFloor?.id || "",
        salleId: selectedSalle?.id || "",
        setupId: activeFolder.id,
        hasCurrent: true,
      });
      return;
    }

    setAffectPicker({
      step: 1,
      floorId: selectedFloor?.id || "",
      salleId: "",
      setupId: "",
      hasCurrent: false,
    });
  };

  const saveToCurrentSetup = () => {
    if (!activeFolder) {
      toast.warning(t("reservationAffectNoCurrentSetup"));
      return;
    }
    const ok = applyDesignToSetup(activeFolder, selectedLayoutKey);
    if (ok) closeAffectPicker();
  };

  const openAffectPickerChooseOther = () => {
    setAffectPicker((prev) => ({
      step: 1,
      floorId: selectedFloor?.id || prev?.floorId || "",
      salleId: "",
      setupId: "",
      hasCurrent: Boolean(prev?.hasCurrent || activeFolder),
    }));
  };

  const closeAffectPicker = () => setAffectPicker(null);

  const affectPickerFloor =
    affectPicker?.floorId
      ? floors.find((floor) => floor.id === affectPicker.floorId) || null
      : null;

  const affectPickerRooms = affectPickerFloor?.salles || [];

  const affectPickerRoom =
    affectPicker?.salleId
      ? affectPickerRooms.find((salle) => salle.id === affectPicker.salleId) ||
        null
      : null;

  const affectPickerLayoutKey =
    affectPickerFloor && affectPickerRoom
      ? `${affectPickerFloor.id}:${affectPickerRoom.id}`
      : "";

  const affectPickerSetups = affectPickerLayoutKey
    ? normalizeFolderList(planFolders[affectPickerLayoutKey] || [])
    : [];

  const affectPickerLabels = affectPickerLayoutKey
    ? resolveFloorSalleNames(affectPickerLayoutKey)
    : { floorName: "", salleName: "" };

  const confirmAffectPicker = () => {
    if (!affectPicker?.setupId) {
      toast.warning(t("reservationAffectPickRequired"));
      return;
    }
    const folder =
      affectPickerSetups.find((item) => item.id === affectPicker.setupId) ||
      findFolderInStore(affectPicker.setupId)?.folder;
    if (!folder) {
      toast.warning(t("reservationAffectPickRequired"));
      return;
    }
    const ok = applyDesignToSetup(folder, affectPickerLayoutKey);
    if (ok) closeAffectPicker();
  };

  /** Create a new setup in the picker room and save the current floor plan into it. */
  const handleCreateSetupFromAffectPicker = async () => {
    if (!affectPickerFloor || !affectPickerRoom || !affectPickerLayoutKey) {
      toast.warning(t("reservationFoldersPickFloorSalle"));
      return;
    }
    if (affectPickerSetups.length >= MAX_FOLDERS_PER_SALLE) {
      toast.warning(t("reservationFolderLimit"));
      return;
    }

    const tableEntries = getSalleTablesSnapshot(tables);
    if (!tableEntries.length && floorElements.length === 0) {
      toast.warning(t("reservationAffectNoDesign"));
      return;
    }

    const name = await promptFolderName({
      title: t("reservationAffectCreateNew"),
      confirmText: t("reservationAffectCreateAndSave"),
    });
    if (!name) return;
    if (findFolderByName(name, "", affectPickerLayoutKey)) {
      toast.warning(t("reservationFolderNameExists", { name }));
      return;
    }

    const labels = resolveFloorSalleNames(affectPickerLayoutKey);
    const folder = buildFolderVersion(
      {
        id: `folder-${Date.now()}`,
        name,
        createdAt: new Date().toISOString(),
        floorId: labels.floorId,
        salleId: labels.salleId,
        floorName: labels.floorName,
        salleName: labels.salleName,
      },
      tableEntries,
      {
        doorPosition,
        floorElements,
        floorId: labels.floorId,
        salleId: labels.salleId,
        floorName: labels.floorName,
        salleName: labels.salleName,
      }
    );

    const store = readFoldersStore();
    commitFoldersStore({
      ...store,
      [affectPickerLayoutKey]: [
        ...(store[affectPickerLayoutKey] || []),
        folder,
      ],
    });

    // Switch to the target floor/room when creating outside the current one
    if (affectPickerLayoutKey !== selectedLayoutKey) {
      pendingFolderOpenRef.current = folder.id;
      setSelectedFloorId(labels.floorId);
      setSelectedSalleId(labels.salleId);
    } else {
      setActiveFolderId(folder.id);
      setActiveDispositionId("");
    }

    setUnplacedTableIds([]);
    setLayoutDirty(false);
    toast.success(
      t("reservationAffectSuccess", {
        name,
        floor: labels.floorName,
        salle: labels.salleName,
      })
    );
    closeAffectPicker();
  };

  /** Exit folder mode — keep the plan on screen, work without a folder. */
  const handleClearFolderSelection = () => {
    if (!activeFolderId) return;
    setActiveFolderId("");
    setUnplacedTableIds([]);
    setLayoutDirty(false);
    toast.info(t("reservationFolderNoneActive"));
  };

  const handlePlaceUnplacedTable = (table) => {
    if (!table?._id) return;
    const tableId = normalizeTableId(table._id);
    const column = unplacedTables.findIndex(
      (item) => normalizeTableId(item._id) === tableId
    );
    const placeIndex = Math.max(0, column);
    setTables((prev) =>
      prev.map((item) =>
        normalizeTableId(item._id) === tableId
          ? {
              ...item,
              position: {
                x: 20 + (placeIndex % 4) * 18,
                y: 22 + Math.floor(placeIndex / 4) * 18,
              },
            }
          : item
      )
    );
    setUnplacedTableIds((prev) =>
      prev.filter((id) => normalizeTableId(id) !== tableId)
    );
    setSelectedTableId(table._id);
    setLayoutDirty(true);
  };

  const handleRenameFolder = async (event, folder) => {
    event.stopPropagation();
    if (!folder?.id) return;
    const name = await promptFolderName({
      title: t("reservationFolderRenameTitle"),
      confirmText: t("reservationFolderRenameAction"),
      defaultName: folder.name || "",
    });
    if (!name) return;
    if (findFolderByName(name, folder.id)) {
      toast.warning(t("reservationFolderNameExists", { name }));
      return;
    }
    const store = readFoldersStore();
    const located = findFolderInStore(folder.id, store);
    const layoutKey = located?.layoutKey || selectedLayoutKey;
    commitFoldersStore({
      ...store,
      [layoutKey]: (store[layoutKey] || []).map((item) =>
        item.id === folder.id
          ? { ...item, name, updatedAt: new Date().toISOString() }
          : item
      ),
    });
    toast.success(t("reservationFolderRenamed", { name }));
    void logAdminHistoryAction({
      action: "plan_floor_setup.update",
      description: `Renamed plan floor setup to "${name}"`,
      metadata: {
        folderId: folder.id,
        name,
        previousName: folder.name || "",
        layoutKey,
      },
    });
  };

  const handleArchiveFolder = async (event, folderId) => {
    event.stopPropagation();
    if (!folderId || savingLayout) return;

    const located = findFolderInStore(folderId);
    const folderName =
      located?.folder?.name || t("reservationFolder");

    const result = await Swal.fire({
      title: t("areYouSure") || "Are you sure?",
      text: t("reservationFolderArchiveConfirm", { name: folderName }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("reservationFolderArchive"),
      cancelButtonText: t("cancel") || "Cancel",
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#94a3b8",
      heightAuto: false,
      customClass: { container: "reservation-swal-on-top" },
    });
    if (!result.isConfirmed) return;

    const archived = archivePlanFloorSetup(folderId);
    if (!archived.ok) {
      toast.error(t("reservationFolderArchiveError"));
      return;
    }

    // Keep React state in sync with localStorage after util write
    commitFoldersStore(readFoldersStore());

    if (activeFolderId === folderId) {
      setActiveFolderId("");
      setUnplacedTableIds([]);
    }
    toast.success(
      t("reservationFolderArchived", { name: folderName })
    );
    void logAdminHistoryAction({
      action: "plan_floor_setup.archive",
      description: `Archived plan floor setup "${folderName}"`,
      metadata: {
        folderId,
        name: folderName,
        layoutKey: archived.layoutKey || located?.layoutKey || "",
      },
    });
  };

  const handleOpenFolder = async (folder, layoutKey = selectedLayoutKey) => {
    if (!folder || savingLayout) return;

    const scrollFloorIntoView = () => {
      requestAnimationFrame(() => {
        floorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      });
    };

    if (layoutKey && layoutKey !== selectedLayoutKey) {
      const [floorId, salleId] = String(layoutKey).split(":");
      if (!floorId || !salleId) return;
      if (activeFolderId) {
        writeFolderSnapshot(activeFolderId, {
          tableEntries: getSalleTablesSnapshot(tables),
        });
      }
      pendingFolderOpenRef.current = folder.id;
      setSelectedFloorId(floorId);
      setSelectedSalleId(salleId);
      scrollFloorIntoView();
      return;
    }

    const located = findFolderInStore(folder.id);
    const latest = located?.folder || folder;

    // Clicking the active folder again = leave folder mode, keep plan as-is
    if (latest.id === activeFolderId) {
      handleClearFolderSelection();
      return;
    }

    if (activeFolderId && activeFolderId !== latest.id) {
      writeFolderSnapshot(activeFolderId, {
        tableEntries: getSalleTablesSnapshot(tables),
      });
    }

    setActiveDispositionId("");
    scrollFloorIntoView();

    if (!latest.tables?.length) {
      setActiveFolderId(latest.id);
      setUnplacedTableIds([]);
      setSelectedTableId(null);
      setPopupTableId(null);
      toast.info(
        t("reservationFolderSelectedEmpty", {
          name: latest.name || t("reservationFolder"),
        })
      );
      return;
    }

    setUnplacedTableIds([]);
    await applyLayoutSnapshot(latest, {
      isDefaultApply: false,
      dispositionId: "",
      folderId: latest.id,
    });
    scrollFloorIntoView();
  };

  useEffect(() => {
    const pendingId = pendingFolderOpenRef.current;
    if (!pendingId || savingLayout) return;

    const folder = (planFolders[selectedLayoutKey] || []).find(
      (item) => item.id === pendingId
    );
    if (!folder) return;

    pendingFolderOpenRef.current = null;
    const timer = window.setTimeout(() => {
      void handleOpenFolder(folder, selectedLayoutKey);
    }, 0);

    return () => window.clearTimeout(timer);
    // Open the pending folder once the target floor/salle is selected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayoutKey, planFolders, savingLayout]);

  const handleSaveAsDefaultDisposition = () => {
    if (salleTables.length === 0) {
      toast.warning(t("reservationDispositionNoTables"));
      return;
    }
    if (activeFolderId) safeguardActiveFolder();

    const hadDefault = Boolean(defaultLayoutSnapshot?.tables?.length);
    persistDefaultLayout(
      buildLayoutSnapshot(
        displayTables.length > 0 ? displayTables : salleTables,
        doorPosition,
        floorElements
      )
    );
    setUnplacedTableIds([]);
    setActiveDispositionId(DEFAULT_LAYOUT_ACTIVE_ID);
    setActiveFolderId("");
    setLayoutDirty(false);
    toast.success(
      hadDefault
        ? t("reservationDefaultDispositionUpdated")
        : t("reservationDefaultDispositionSaved")
    );
  };

  const syncDispositionAfterLayoutSave = () => {
    if (activeFolderId) {
      writeFolderSnapshot(activeFolderId, {
        tableEntries: getSalleTablesSnapshot(tables),
        door: doorPosition,
        elements: floorElements,
      });
      return;
    }
    const activeVersion = currentDispositions.find(
      (item) => item.id === activeDispositionId
    );
    if (activeVersion) {
      updateDispositionSnapshot(activeVersion.id);
    }
  };

  const handleSaveLayout = async () => {
    const folderIdToKeep = activeFolderId;
    const tableEntries = getSalleTablesSnapshot(tables);
    setSavingLayout(true);
    try {
      if (tables.length > 0) {
        await saveReservationLayout(tables);
      }
      localStorage.setItem(DOOR_STORAGE_KEY, JSON.stringify(doorPosition));
      localStorage.setItem(FLOOR_ELEMENTS_STORAGE_KEY, JSON.stringify(floorElements));
      const savedLayouts = JSON.parse(
        localStorage.getItem(FLOOR_LAYOUTS_STORAGE_KEY) || "{}"
      );
      localStorage.setItem(
        FLOOR_LAYOUTS_STORAGE_KEY,
        JSON.stringify({
          ...savedLayouts,
          [selectedLayoutKey]: {
            doorPosition,
            floorElements,
          },
        })
      );

      if (folderIdToKeep) {
        const savedFolder = writeFolderSnapshot(folderIdToKeep, {
          tableEntries,
          door: doorPosition,
          elements: floorElements,
        });
        setActiveFolderId(folderIdToKeep);
        setUnplacedTableIds([]);

        if (!tableEntries.length) {
          toast.warning(t("reservationFolderEmptyHint"));
        } else {
          toast.success(
            t("reservationFolderVersionSaved", {
              name: savedFolder?.name || t("reservationFolder"),
            })
          );
        }
      } else {
        syncDispositionAfterLayoutSave();
        toast.success(t("reservationLayoutSaved"));
      }

      setLayoutDirty(false);
      await Promise.all([loadTables(), loadAvailability()]);
    } catch (error) {
      toast.error(error?.message || t("reservationLayoutSaveError"));
    } finally {
      setSavingLayout(false);
    }
  };

  const applyLayoutSnapshot = async (
    snapshot,
    { isDefaultApply = false, dispositionId = "", folderId = "" } = {}
  ) => {
    if (!snapshot?.tables?.length || savingLayout) return false;

    const tableLayout = buildTableLayoutMap(snapshot.tables);
    let matchedCount = 0;

    const nextTables = tables.map((table) => {
      const tableFloor = table.floor || DEFAULT_FLOOR_ID;
      const tableSalle = table.zone || DEFAULT_SALLE_ID;
      const savedTable = tableLayout.get(normalizeTableId(table._id));
      if (
        !savedTable ||
        tableFloor !== selectedFloor?.id ||
        tableSalle !== selectedSalle?.id
      ) {
        return table;
      }

      matchedCount += 1;
      return {
        ...table,
        position: {
          x: Number(savedTable.position?.x ?? table.position?.x ?? 50),
          y: Number(savedTable.position?.y ?? table.position?.y ?? 50),
        },
        size: {
          width: Number(savedTable.size?.width ?? table.size?.width ?? 88),
          height: Number(savedTable.size?.height ?? table.size?.height ?? 88),
        },
        rotation: Number(savedTable.rotation ?? table.rotation ?? 0),
      };
    });

    if (matchedCount === 0) {
      toast.warning(t("reservationDefaultDispositionRestoreFailed"));
      return false;
    }

    const nextDoorPosition = snapshot.doorPosition || DEFAULT_DOOR_POSITION;
    const nextFloorElements = normalizeFloorElements(snapshot.floorElements || []);

    draggedElementRef.current = null;
    setTables(nextTables);
    setDoorPosition(nextDoorPosition);
    setFloorElements(nextFloorElements);
    setSavingLayout(true);
    try {
      const saved = await saveReservationLayout(nextTables);
      setTables(Array.isArray(saved) ? saved : nextTables);
      localStorage.setItem(DOOR_STORAGE_KEY, JSON.stringify(nextDoorPosition));
      localStorage.setItem(FLOOR_ELEMENTS_STORAGE_KEY, JSON.stringify(nextFloorElements));
      const savedLayouts = JSON.parse(
        localStorage.getItem(FLOOR_LAYOUTS_STORAGE_KEY) || "{}"
      );
      localStorage.setItem(
        FLOOR_LAYOUTS_STORAGE_KEY,
        JSON.stringify({
          ...savedLayouts,
          [selectedLayoutKey]: {
            doorPosition: nextDoorPosition,
            floorElements: nextFloorElements,
          },
        })
      );
      setLayoutDirty(false);
      if (isDefaultApply) {
        setActiveDispositionId(DEFAULT_LAYOUT_ACTIVE_ID);
        setActiveFolderId("");
        setUnplacedTableIds([]);
      } else if (folderId) {
        setActiveFolderId(folderId);
        setActiveDispositionId("");
        // Folder snapshots include the full salle layout — show all restored tables
        setUnplacedTableIds([]);
      } else {
        setActiveDispositionId(dispositionId || activeDispositionId);
        setActiveFolderId("");
        setUnplacedTableIds([]);
      }
      await loadAvailability();
      toast.success(
        isDefaultApply
          ? t("reservationDefaultDispositionLoaded")
          : folderId
            ? t("reservationFolderOpened", {
                name: snapshot?.name || t("reservationFolder"),
              })
            : t("reservationDispositionLoaded")
      );
      return true;
    } catch (error) {
      setLayoutDirty(true);
      toast.error(error?.message || t("reservationLayoutSaveError"));
      return false;
    } finally {
      setSavingLayout(false);
    }
  };

  const handleApplyDefaultDisposition = () => {
    const store = readDefaultLayoutsStore();
    const snapshot = store[selectedLayoutKey];
    if (!snapshot?.tables?.length) {
      toast.warning(t("reservationDefaultDispositionMissing"));
      return;
    }
    if (activeFolderId) safeguardActiveFolder();
    applyLayoutSnapshot(snapshot, { isDefaultApply: true });
  };

  const handleSaveTableSize = async () => {
    if (!popupTable) return;
    if (isTableLockedForSlot(popupTable)) {
      toast.warning(t("reservationTableLocked"));
      return;
    }
    if (!popupName.trim()) {
      toast.warning(t("reservationTableRequired"));
      return;
    }
    setSubmittingPopup(true);
    try {
      const payload = {
        name: popupName.trim(),
        tableNumber: popupTable.tableNumber,
        seats: Math.max(1, Number(popupSeats) || 1),
        shape: popupTable.shape,
        manualStatus: popupTable.manualStatus || "available",
        zone: popupTable.zone || selectedSalle?.id || DEFAULT_SALLE_ID,
        floor: popupTable.floor || selectedFloor?.id || DEFAULT_FLOOR_ID,
        position: popupTable.position || { x: 50, y: 50 },
        size: {
          width: Number(popupSize.width) || 88,
          height: Number(popupSize.height) || 88,
        },
        rotation: Number(popupTable.rotation) || 0,
        color: popupColor.trim()
          ? normalizeTableHex(popupColor, defaultTableColor)
          : "",
      };
      const updatedTable = await updateReservationTable(popupTable._id, payload);
      setTables((prev) =>
        prev.map((table) => (table._id === updatedTable._id ? updatedTable : table))
      );
      await loadAvailability();
      toast.success(t("reservationTableUpdated"));
    } catch (error) {
      toast.error(error?.message || t("reservationTableSaveError"));
    } finally {
      setSubmittingPopup(false);
    }
  };

  const scrollToFloorPlan = () => {
    floorRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  const handleQuickAddTable = async (option, index) => {
    setSubmittingTable(true);
    try {
      const tableNumber =
        (await fetchNextReservationTableNumber()) || nextTableNumber;
      await createReservationTable({
        name: `${t(option.labelKey)} ${tableNumber}`,
        tableNumber,
        seats: option.seats,
        shape: option.value,
        manualStatus: "available",
        zone: selectedSalle?.id || DEFAULT_SALLE_ID,
        floor: selectedFloor?.id || DEFAULT_FLOOR_ID,
        position: {
          x: 18 + (index % 3) * 24,
          y: 24 + Math.floor(index / 3) * 24,
        },
        size: { width: option.width, height: option.height },
        rotation: 0,
      });
      toast.success(t("reservationTableCreated"));
      await Promise.all([loadTables(), loadAvailability()]);
      if (activeFolderId) {
        setLayoutDirty(true);
      }
      scrollToFloorPlan();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationTableSaveError")));
    } finally {
      setSubmittingTable(false);
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu={t("reservations")} motherMenu={t("navPointOfSale")} />

      <div className="reservation-page" data-tour="tour-page-reservations">
        <div className="reservation-overview">
          <div className="reservation-stats">
            <article className="reservation-stat is-available">
              <strong>{availableTablesCount}</strong>
              <span>{t("reservationStatAvailable")}</span>
            </article>
            <article className="reservation-stat is-total">
              <strong>{displayTables.length}</strong>
              <span>{t("reservationStatTotal")}</span>
            </article>
            <article className="reservation-stat is-pending">
              <strong>{reservationStats.pending}</strong>
              <span>{t("reservationStatPending")}</span>
            </article>
            <article className="reservation-stat is-active">
              <strong>{reservationStats.active}</strong>
              <span>{t("reservationStatConfirmed")}</span>
            </article>
          </div>
          <div className="reservation-overview__actions">
            <button
              type="button"
              className={`reservation-overview__toggle${
                showReservationsCalendar ? " is-active" : ""
              }`}
              onClick={() => setShowReservationsCalendar(true)}
            >
              <i className="fa fa-calendar-alt" aria-hidden="true" />
              {t("reservationOpenCalendar")}
            </button>
            <button
              type="button"
              className={`reservation-overview__toggle${
                showReservationsList ? " is-active" : ""
              }`}
              onClick={handleToggleReservationsList}
            >
              <i className="fa fa-list-ul" aria-hidden="true" />
              {showReservationsList
                ? t("reservationHideAllReservations")
                : t("reservationShowAllReservations")}
              {!loadingReservations && reservationStats.total > 0 ? (
                <span className="reservation-overview__count">{reservationStats.total}</span>
              ) : null}
            </button>
          </div>
        </div>

      {showReservationsList && (
        <section className="reservation-panel">
          <div className="reservation-panel__head">
            <div>
              <h2>{t("reservationClientReservations")}</h2>
              <p>{t("reservationClientReservationsHint")}</p>
            </div>
            <div className="reservation-panel__head-actions">
              {!loadingReservations ? (
                <span className="reservation-panel__count">
                  {t("reservationClientCount", { count: filteredReservations.length })}
                </span>
              ) : null}
              <button
                type="button"
                className="reservation-panel__add"
                onClick={handleOpenNewReservationPopup}
              >
                <i className="fa fa-plus" aria-hidden="true" />
                {t("reservationAddReservationTitle")}
              </button>
              <button
                type="button"
                className="reservation-panel__refresh"
                disabled={loadingReservations}
                onClick={loadReservations}
              >
                <i className={`fa fa-sync-alt${loadingReservations ? " fa-spin" : ""}`} />
                {loadingReservations ? t("loading") : t("refresh")}
              </button>
            </div>
          </div>

          <div className="reservation-panel__filters">
            <div className="reservation-panel__search">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                type="search"
                className="form-control"
                value={listSearch}
                onChange={(event) => setListSearch(event.target.value)}
                placeholder={t("reservationListSearchPlaceholder")}
                aria-label={t("reservationListSearchPlaceholder")}
              />
            </div>
            <select
              className="reservation-panel__select"
              value={listStatusFilter}
              onChange={(event) => setListStatusFilter(event.target.value)}
              aria-label={t("reservationArchivedFilterStatus")}
            >
              <option value="">
                {t("reservationListFilterAll")} ({reservations.length})
              </option>
              {RESERVATION_STATUS_OPTIONS.map((status) => {
                const count = reservations.filter((item) => item.status === status).length;
                return (
                  <option key={status} value={status}>
                    {t(`reservationStatus_${status}`)} ({count})
                  </option>
                );
              })}
            </select>
            {listSearch || listStatusFilter ? (
              <button
                type="button"
                className="reservation-panel__clear"
                onClick={() => {
                  setListSearch("");
                  setListStatusFilter("");
                }}
              >
                {t("reservationArchivedClearFilters")}
              </button>
            ) : null}
          </div>

            {loadingReservations ? (
              <div className="reservation-list-empty reservation-list-empty--panel">
                <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                <strong>{t("loading")}…</strong>
              </div>
            ) : reservations.length === 0 ? (
              <div className="reservation-list-empty reservation-list-empty--panel">
                <i className="fa fa-calendar-o" aria-hidden="true" />
                <strong>{t("reservationNoClientReservations")}</strong>
                <p>{t("reservationEmptyHint")}</p>
              </div>
            ) : filteredReservations.length === 0 ? (
              <div className="reservation-list-empty reservation-list-empty--panel">
                <i className="fa fa-filter" aria-hidden="true" />
                <strong>{t("reservationFilterEmpty")}</strong>
                <button
                  type="button"
                  className="reservation-panel__clear reservation-panel__clear--inline"
                  onClick={() => {
                    setListSearch("");
                    setListStatusFilter("");
                  }}
                >
                  {t("reservationArchivedClearFilters")}
                </button>
              </div>
            ) : (
              <div className="reservation-list-paginated">
              <div className="reservation-table-shell reservation-table-shell--panel">
                <table className="reservation-data-table">
                  <thead>
                    <tr>
                      <th>
                        <span className="reservation-th">
                          <i className="fa fa-user" />
                          {t("reservationFormFullName")}
                        </span>
                      </th>
                      <th>
                        <span className="reservation-th">
                          <i className="fa fa-phone" />
                          {t("reservationFormPhone")}
                        </span>
                      </th>
                      <th>
                        <span className="reservation-th">
                          <i className="fa fa-calendar" />
                          {t("reservationFormDate")}
                        </span>
                      </th>
                      <th>
                        <span className="reservation-th">
                          <i className="fa fa-users" />
                          {t("reservationFormGuests")}
                        </span>
                      </th>
                      <th>
                        <span className="reservation-th">
                          <i className="fa fa-map-marker" />
                          {t("reservationFormSeating")}
                        </span>
                      </th>
                      <th className="reservation-col-table">
                        <span className="reservation-th">
                          <i className="fas fa-chair" />
                          {t("reservationTable")}
                        </span>
                      </th>
                      <th>
                        <span className="reservation-th">
                          <i className="fa fa-check-circle" />
                          {t("status")}
                        </span>
                      </th>
                      <th>
                        <span className="reservation-th">
                          <i className="fa fa-ellipsis-h" />
                          {t("actions")}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedReservations.map((reservation) => {
                      const start = reservation.startAt
                        ? new Date(reservation.startAt)
                        : null;
                      const validStart = start && !Number.isNaN(start.getTime());
                      const unassigned =
                        !reservation.table && !reservation.tableNumber;
                      const actualDuration = getActualDurationMinutes(reservation);
                      const nameParts = String(reservation.customerName || "")
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean);
                      const initials =
                        nameParts.length === 0
                          ? "?"
                          : nameParts.length === 1
                            ? nameParts[0].slice(0, 2).toUpperCase()
                            : `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
                      const seating = reservation.seatingPreference || "";

                      return (
                        <tr key={reservation._id}>
                          <td>
                            <div className="reservation-td-guest">
                              <span className="reservation-td-avatar" aria-hidden="true">
                                {initials}
                              </span>
                              <div className="reservation-td-guest-block">
                                <strong className="reservation-td-name">
                                  {reservation.customerName || "-"}
                                </strong>
                                {reservation.customerEmail ? (
                                  <span
                                    className="reservation-td-email"
                                    title={reservation.customerEmail}
                                  >
                                    {reservation.customerEmail}
                                  </span>
                                ) : (
                                  <span className="reservation-td-email is-empty">—</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="reservation-td-phone">
                              <i className="fa fa-phone" />
                              {reservation.customerPhone || "-"}
                            </span>
                          </td>
                          <td>
                            <div className="reservation-td-when">
                              <strong className="reservation-td-date">
                                {validStart
                                  ? start.toLocaleDateString([], {
                                      weekday: "short",
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "-"}
                              </strong>
                              <span className="reservation-td-time">
                                <i className="fa fa-clock-o" />
                                {validStart
                                  ? start.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "-"}
                              </span>
                              {actualDuration ? (
                                <span className="reservation-td-duration is-actual">
                                  <i className="fa fa-hourglass-half" />
                                  {`${t("reservationActualDurationLabel")}: ${formatDurationLabel(actualDuration)}`}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <span className="reservation-td-pill is-guests">
                              <i className="fa fa-users" />
                              {reservation.partySize || "-"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`reservation-td-seating${
                                seating ? ` is-${seating}` : ""
                              }`}
                            >
                              <i
                                className={
                                  seating === "terrace"
                                    ? "fa fa-map-marker"
                                    : seating === "outdoor"
                                      ? "fa fa-sun-o"
                                      : "fa fa-th-large"
                                }
                              />
                              {seating
                                ? t(`reservationSeating_${seating}`) || seating
                                : t("reservationSeatingAny")}
                            </span>
                          </td>
                          <td className="reservation-col-table">
                            <div className="reservation-td-table">
                              <ReservationTablePicker
                                value={reservation.table?._id || reservation.table || ""}
                                options={getAssignableTableOptions(reservation)}
                                disabled={updatingReservationId === reservation._id}
                                unassigned={unassigned}
                                tableColor={defaultTableColor}
                                onChange={(nextTableId, tableLabel) => {
                                  if (
                                    nextTableId ===
                                    String(reservation.table?._id || reservation.table || "")
                                  ) {
                                    return;
                                  }
                                  handleReservationFieldUpdate(
                                    reservation,
                                    { table: nextTableId || null },
                                    {
                                      tableLabel:
                                        tableLabel || t("reservationTableUnassigned"),
                                    }
                                  );
                                }}
                                ariaLabel={t("reservationTable")}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="reservation-td-select-wrap is-status">
                              <select
                                className={`reservation-td-select is-status is-${reservation.status}`}
                                value={reservation.status || "pending"}
                                disabled={updatingReservationId === reservation._id}
                                onChange={(event) => {
                                  const nextStatus = event.target.value;
                                  if (nextStatus === reservation.status) return;
                                  handleReservationFieldUpdate(reservation, {
                                    status: nextStatus,
                                  });
                                }}
                                aria-label={t("status")}
                              >
                                {RESERVATION_STATUS_OPTIONS.map((status) => (
                                  <option key={status} value={status}>
                                    {t(`reservationStatus_${status}`)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td>
                            <div className="reservation-td-actions">
                              {(() => {
                                const reply = buildClientReservationReplyContent(
                                  reservation,
                                  t
                                );
                                const gmailHref = buildGmailComposeLink(
                                  reservation.customerEmail,
                                  reply.subject,
                                  reply.emailBody
                                );
                                const whatsappHref = buildWhatsAppLink(
                                  reservation.customerPhone,
                                  reply.whatsappMessage
                                );
                                return (
                                  <>
                                    <a
                                      href={gmailHref || undefined}
                                      className={`reservation-action-icon is-gmail${
                                        gmailHref ? "" : " is-disabled"
                                      }`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={t("reservationActionGmail")}
                                      aria-label={t("reservationActionGmail")}
                                      aria-disabled={!gmailHref}
                                      onClick={(event) => {
                                        if (!gmailHref) {
                                          event.preventDefault();
                                          return;
                                        }
                                        const name =
                                          reservation.customerName ||
                                          reservation.customerEmail ||
                                          "guest";
                                        void logAdminHistoryAction({
                                          action: "reservation.contact_gmail",
                                          description: `Opened Gmail to contact reservation guest "${name}" (${reservation.customerEmail})`,
                                          metadata: {
                                            reservationId: reservation._id,
                                            customerName: reservation.customerName || "",
                                            email: reservation.customerEmail || "",
                                            channel: "gmail",
                                          },
                                        });
                                      }}
                                    >
                                      <i className="fa-brands fa-google" />
                                    </a>
                                    <a
                                      href={whatsappHref || undefined}
                                      className={`reservation-action-icon is-whatsapp${
                                        whatsappHref ? "" : " is-disabled"
                                      }`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={t("reservationActionWhatsApp")}
                                      aria-label={t("reservationActionWhatsApp")}
                                      aria-disabled={!whatsappHref}
                                      onClick={(event) => {
                                        if (!whatsappHref) {
                                          event.preventDefault();
                                          return;
                                        }
                                        const name =
                                          reservation.customerName ||
                                          reservation.customerPhone ||
                                          "guest";
                                        void logAdminHistoryAction({
                                          action: "reservation.contact_whatsapp",
                                          description: `Opened WhatsApp to contact reservation guest "${name}" (${reservation.customerPhone})`,
                                          metadata: {
                                            reservationId: reservation._id,
                                            customerName: reservation.customerName || "",
                                            phone: reservation.customerPhone || "",
                                            channel: "whatsapp",
                                          },
                                        });
                                      }}
                                    >
                                      <i className="fa-brands fa-whatsapp" />
                                    </a>
                                  </>
                                );
                              })()}
                              <button
                                type="button"
                                className="reservation-action-icon is-edit"
                                onClick={() => handleOpenEditReservation(reservation)}
                                title={t("reservationEditBooking")}
                                aria-label={t("reservationEditBooking")}
                              >
                                <i className="fa fa-pencil" />
                              </button>
                              <button
                                type="button"
                                className="reservation-action-icon is-archive"
                                onClick={() => handleArchiveReservation(reservation)}
                                disabled={archivingReservationId === reservation._id}
                                title={t("reservationArchiveAction")}
                                aria-label={t("reservationArchiveAction")}
                              >
                                <i className="fa fa-archive" />
                              </button>
                              <button
                                type="button"
                                className="reservation-view-details-btn"
                                onClick={() => setDetailsReservation(reservation)}
                                title={t("reservationViewDetails")}
                                aria-label={t("reservationViewDetails")}
                              >
                                <i className="fa fa-eye" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="reservation-archived-pagination">
                <div className="reservation-archived-pagination__meta">
                  <span>
                    {t("reservationArchivedShowingCount", {
                      from: listShowingFrom,
                      to: listShowingTo,
                      total: filteredReservations.length,
                    })}
                  </span>
                </div>

                {listTotalPages > 1 ? (
                  <div className="reservation-archived-pagination__btns">
                    <button
                      type="button"
                      className="reservation-archived-page-btn"
                      disabled={listCurrentPage <= 1}
                      onClick={() => setListPage((prev) => Math.max(1, prev - 1))}
                      aria-label={t("previous")}
                    >
                      <i className="fa fa-chevron-left" aria-hidden="true" />
                    </button>

                    {listVisiblePages.map((pageNumber) =>
                      typeof pageNumber === "string" ? (
                        <span
                          key={pageNumber}
                          className="reservation-archived-page-ellipsis"
                          aria-hidden="true"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={pageNumber}
                          type="button"
                          className={`reservation-archived-page-btn${
                            listCurrentPage === pageNumber ? " is-active" : ""
                          }`}
                          onClick={() => setListPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      )
                    )}

                    <button
                      type="button"
                      className="reservation-archived-page-btn"
                      disabled={listCurrentPage >= listTotalPages}
                      onClick={() =>
                        setListPage((prev) => Math.min(listTotalPages, prev + 1))
                      }
                      aria-label={t("next")}
                    >
                      <i className="fa fa-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
              </div>
              </div>
            )}
        </section>
      )}

      {showReservationsCalendar ? (
        <ReservationCalendar
          open={showReservationsCalendar}
          onClose={() => setShowReservationsCalendar(false)}
          onSelectReservation={(reservation) => {
            setShowReservationsCalendar(false);
            setDetailsReservation(reservation);
          }}
        />
      ) : null}

      {detailsReservation && (() => {
        const start = detailsReservation.startAt
          ? new Date(detailsReservation.startAt)
          : null;
        const validStart = start && !Number.isNaN(start.getTime());
        const unassigned =
          !detailsReservation.table && !detailsReservation.tableNumber;
        const tableLabel =
          detailsReservation.table?.name ||
          (detailsReservation.tableNumber
            ? `${t("reservationTable")} ${detailsReservation.tableNumber}`
            : t("reservationTableUnassigned"));
        const clientNote = getClientNoteOnly(detailsReservation);
        const nameParts = String(detailsReservation.customerName || "")
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        const initials =
          nameParts.length === 0
            ? "?"
            : nameParts.length === 1
              ? nameParts[0].slice(0, 2).toUpperCase()
              : `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
        const guestCount = detailsReservation.partySize || 0;
        const guestsLabel =
          guestCount === 1
            ? `${guestCount} ${t("reservationGuestOne")}`
            : `${guestCount || "—"} ${t("reservationGuestMany")}`;
        const dateLabel = validStart
          ? start.toLocaleDateString([], {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "—";
        const timeLabel = validStart
          ? start.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—";
        const seatingLabel = detailsReservation.seatingPreference
          ? t(`reservationSeating_${detailsReservation.seatingPreference}`) ||
            detailsReservation.seatingPreference
          : t("reservationSeatingAny");
        const actualDuration = getActualDurationMinutes(detailsReservation);

        return (
          <div
            className="reservation-popup-backdrop"
            onMouseDown={() => setDetailsReservation(null)}
          >
            <div
              className="reservation-popup reservation-details-popup"
              role="dialog"
              aria-modal="true"
              aria-labelledby="reservation-details-popup-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="reservation-details-close"
                onClick={() => setDetailsReservation(null)}
                aria-label={t("close")}
              >
                <i className="fa fa-times" />
              </button>

              <div className="reservation-details-hero">
                <span className="reservation-details-avatar" aria-hidden="true">
                  {initials}
                </span>
                <div className="reservation-details-hero-text">
                  <h4 id="reservation-details-popup-title">
                    {detailsReservation.customerName || "—"}
                  </h4>
                  <p>{t("reservationDetailsTitle")}</p>
                  <div className="reservation-details-badges">
                    <span
                      className={`reservation-td-status is-${detailsReservation.status}`}
                    >
                      <i className="fa fa-circle" />
                      {t(`reservationStatus_${detailsReservation.status}`)}
                    </span>
                    <span
                      className={`reservation-td-pill${
                        unassigned ? " is-warn" : " is-table"
                      }`}
                    >
                      <i className="fas fa-chair" />
                      {tableLabel}
                    </span>
                  </div>
                </div>
              </div>

              <section className="reservation-details-section">
                <div className="reservation-details-section-head">
                  <span className="reservation-details-section-icon is-contact">
                    <i className="fa fa-user" />
                  </span>
                  <h5>{t("reservationDetailsContact")}</h5>
                </div>
                <div className="reservation-details-cards">
                  <div className="reservation-details-card is-phone">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-phone">
                        <i className="fa fa-phone" />
                      </span>
                      <span>{t("reservationFormPhone")}</span>
                    </div>
                    <strong>{detailsReservation.customerPhone || "—"}</strong>
                  </div>
                  <div className="reservation-details-card is-email">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-email">
                        <i className="fa fa-envelope" />
                      </span>
                      <span>{t("reservationFormEmail")}</span>
                    </div>
                    <strong
                      className="is-email"
                      title={detailsReservation.customerEmail || undefined}
                    >
                      {detailsReservation.customerEmail || "—"}
                    </strong>
                  </div>
                  <div className="reservation-details-card is-full is-preferred">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-preferred">
                        <i className="fa fa-comments" />
                      </span>
                      <span>{t("reservationFormPreferredContact")}</span>
                    </div>
                    {detailsReservation.preferredContact ? (
                      <strong>
                        <span
                          className={`reservation-td-contact is-${detailsReservation.preferredContact}`}
                        >
                          <i
                            className={
                              detailsReservation.preferredContact === "email"
                                ? "fa fa-envelope"
                                : detailsReservation.preferredContact ===
                                    "whatsapp"
                                  ? "fa-brands fa-whatsapp"
                                  : "fa fa-phone"
                            }
                          />
                          {t(
                            `reservationContact_${detailsReservation.preferredContact}`
                          ) || detailsReservation.preferredContact}
                        </span>
                      </strong>
                    ) : (
                      <strong className="is-muted">—</strong>
                    )}
                  </div>
                </div>
              </section>

              <section className="reservation-details-section">
                <div className="reservation-details-section-head">
                  <span className="reservation-details-section-icon is-booking">
                    <i className="fa fa-calendar" />
                  </span>
                  <h5>{t("reservationDetailsBooking")}</h5>
                </div>
                <div className="reservation-details-cards">
                  <div className="reservation-details-card is-date">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-date">
                        <i className="fa fa-calendar" />
                      </span>
                      <span>{t("reservationFormDate")}</span>
                    </div>
                    <strong>{dateLabel}</strong>
                    <em className="reservation-details-time">
                      <i className="fa fa-clock-o" />
                      {timeLabel}
                    </em>
                  </div>
                  <div className="reservation-details-card is-guests">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-guests">
                        <i className="fa fa-users" />
                      </span>
                      <span>{t("reservationFormGuests")}</span>
                    </div>
                    <strong>{guestsLabel}</strong>
                  </div>
                  <div className="reservation-details-card is-duration is-actual">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-duration">
                        <i className="fa fa-hourglass-end" />
                      </span>
                      <span>{t("reservationActualDurationLabel")}</span>
                    </div>
                    <strong>{formatDurationLabel(actualDuration)}</strong>
                  </div>
                  <div className="reservation-details-card is-full is-seating">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-seating">
                        <i
                          className={
                            detailsReservation.seatingPreference === "terrace"
                              ? "fa fa-map-marker"
                              : detailsReservation.seatingPreference === "outdoor"
                                ? "fa fa-sun-o"
                                : "fa fa-th-large"
                          }
                        />
                      </span>
                      <span>{t("reservationFormSeating")}</span>
                    </div>
                    <strong>{seatingLabel}</strong>
                  </div>
                </div>
              </section>

              <section className="reservation-details-section is-last">
                <div className="reservation-details-section-head">
                  <span className="reservation-details-section-icon is-notes">
                    <i className="fa fa-sticky-note" />
                  </span>
                  <h5>{t("notes")}</h5>
                </div>
                <div className="reservation-details-cards">
                  <div className="reservation-details-card is-full is-notes">
                    <div className="reservation-details-card-top">
                      <span className="reservation-details-field-icon is-notes">
                        <i className="fa fa-comment" />
                      </span>
                      <span>{t("reservationFormNotes")}</span>
                    </div>
                    {clientNote ? (
                      <strong className="is-note">“{clientNote}”</strong>
                    ) : (
                      <strong className="is-muted">{t("reservationNoNote")}</strong>
                    )}
                  </div>
                </div>
              </section>

              <div className="reservation-details-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary reservation-details-archive-btn"
                  onClick={() => handleArchiveReservation(detailsReservation)}
                  disabled={archivingReservationId === detailsReservation._id}
                >
                  <i className="fa fa-archive" />
                  {t("reservationArchiveAction")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary reservation-details-edit-btn"
                  onClick={() => handleOpenEditReservation(detailsReservation)}
                >
                  <i className="fa fa-pencil" />
                  {t("reservationEditBooking")}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showNewReservationPopup ? (
        <div
          className="reservation-popup-backdrop"
          onMouseDown={handleCloseNewReservationPopup}
        >
          <div
            className="reservation-popup reservation-add-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reservation-add-popup-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="reservation-details-close"
              onClick={handleCloseNewReservationPopup}
              aria-label={t("close")}
            >
              <i className="fa fa-times" />
            </button>

            <form
              className="reservation-add-form"
              onSubmit={handleSubmitNewReservation}
              noValidate
            >
              <div className="reservation-add-form__hero">
                <div className="reservation-add-form__hero-badge">
                  <i className="fa fa-calendar-plus-o" aria-hidden="true" />
                  {t("reservationCreateBooking")}
                </div>
                <h3 id="reservation-add-popup-title">
                  {newReservationLockedTable
                    ? t("reservationReserveTable")
                    : t("reservationAddReservationTitle")}
                </h3>
                <p>
                  {newReservationLockedTable
                    ? t("reservationReserveTableHint", {
                        table:
                          newReservationLockedTable.name ||
                          `${t("reservationTable")} ${
                            newReservationLockedTable.tableNumber || ""
                          }`.trim(),
                      })
                    : t("reservationAddReservationHint")}
                </p>
              </div>

              <div className="reservation-add-form__body">
                <section className="reservation-add-form__card">
                  <div className="reservation-add-form__section-head">
                    <span className="reservation-add-form__section-icon is-contact">
                      <i className="fa fa-user" aria-hidden="true" />
                    </span>
                    <h4>{t("reservationDetailsContact")}</h4>
                  </div>
                  <div className="reservation-add-form__grid">
                    <label
                      className={`reservation-add-form__field${
                        newReservationErrors.customerName ? " has-error" : ""
                      }`}
                    >
                      <span>
                        {t("reservationFormFullName")}
                        <RequiredMark />
                      </span>
                      <input
                        className="form-control"
                        value={newReservationForm.customerName}
                        onChange={(event) =>
                          handleNewReservationChange("customerName", event.target.value)
                        }
                        aria-invalid={Boolean(newReservationErrors.customerName)}
                        aria-required="true"
                      />
                      {newReservationErrors.customerName ? (
                        <em className="reservation-add-form__error">
                          {newReservationErrors.customerName}
                        </em>
                      ) : null}
                    </label>
                    <label
                      className={`reservation-add-form__field${
                        newReservationErrors.customerPhone ? " has-error" : ""
                      }`}
                    >
                      <span>
                        {t("reservationFormPhone")}
                        <RequiredMark />
                      </span>
                      <input
                        className="form-control"
                        value={newReservationForm.customerPhone}
                        onChange={(event) =>
                          handleNewReservationChange("customerPhone", event.target.value)
                        }
                        aria-invalid={Boolean(newReservationErrors.customerPhone)}
                        aria-required="true"
                      />
                      {newReservationErrors.customerPhone ? (
                        <em className="reservation-add-form__error">
                          {newReservationErrors.customerPhone}
                        </em>
                      ) : null}
                    </label>
                    <label
                      className={`reservation-add-form__field${
                        newReservationErrors.customerEmail ? " has-error" : ""
                      }`}
                    >
                      <span>{t("reservationFormEmail")}</span>
                      <input
                        type="email"
                        className="form-control"
                        value={newReservationForm.customerEmail}
                        onChange={(event) =>
                          handleNewReservationChange("customerEmail", event.target.value)
                        }
                        aria-invalid={Boolean(newReservationErrors.customerEmail)}
                      />
                      {newReservationErrors.customerEmail ? (
                        <em className="reservation-add-form__error">
                          {newReservationErrors.customerEmail}
                        </em>
                      ) : null}
                    </label>
                  </div>
                </section>

                <section className="reservation-add-form__card">
                  <div className="reservation-add-form__section-head">
                    <span className="reservation-add-form__section-icon is-booking">
                      <i className="fa fa-calendar" aria-hidden="true" />
                    </span>
                    <h4>{t("reservationDetailsBooking")}</h4>
                  </div>
                  <div className="reservation-add-form__grid">
                    <label
                      className={`reservation-add-form__field${
                        newReservationErrors.date ? " has-error" : ""
                      }`}
                    >
                      <span>
                        {t("reservationFormDate")}
                        <RequiredMark />
                      </span>
                      <input
                        type="date"
                        className="form-control"
                        value={newReservationForm.date}
                        onChange={(event) =>
                          handleNewReservationChange("date", event.target.value)
                        }
                        aria-invalid={Boolean(newReservationErrors.date)}
                        aria-required="true"
                      />
                      {newReservationErrors.date ? (
                        <em className="reservation-add-form__error">
                          {newReservationErrors.date}
                        </em>
                      ) : null}
                    </label>
                    <label
                      className={`reservation-add-form__field${
                        newReservationErrors.time ? " has-error" : ""
                      }`}
                    >
                      <span>
                        {t("reservationFormTime")}
                        <RequiredMark />
                      </span>
                      <input
                        type="time"
                        className="form-control"
                        value={newReservationForm.time}
                        onChange={(event) =>
                          handleNewReservationChange("time", event.target.value)
                        }
                        aria-invalid={Boolean(newReservationErrors.time)}
                        aria-required="true"
                      />
                      {newReservationErrors.time ? (
                        <em className="reservation-add-form__error">
                          {newReservationErrors.time}
                        </em>
                      ) : null}
                    </label>
                    <label className="reservation-add-form__field">
                      <span>{t("reservationPlannedDurationLabel")}</span>
                      <select
                        className="form-control"
                        value={newReservationForm.durationMinutes}
                        onChange={(event) =>
                          handleNewReservationChange(
                            "durationMinutes",
                            Number(event.target.value)
                          )
                        }
                      >
                        {DURATION_OPTIONS.map((minutes) => (
                          <option key={minutes} value={minutes}>
                            {formatDurationLabel(minutes)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="reservation-add-form__assignment">
                    <label
                      className={`reservation-add-form__field${
                        newReservationErrors.partySize ? " has-error" : ""
                      }`}
                    >
                      <span>
                        {t("reservationFormGuests")}
                        <RequiredMark />
                      </span>
                      <input
                        type="number"
                        min="1"
                        max={newReservationTableMaxSeats}
                        className="form-control"
                        value={newReservationForm.partySize}
                        onChange={(event) =>
                          handleNewReservationChange(
                            "partySize",
                            event.target.value === ""
                              ? ""
                              : Math.max(1, Number(event.target.value) || 1)
                          )
                        }
                        aria-invalid={Boolean(newReservationErrors.partySize)}
                        aria-required="true"
                      />
                      {newReservationErrors.partySize ? (
                        <em className="reservation-add-form__error">
                          {newReservationErrors.partySize}
                        </em>
                      ) : null}
                    </label>
                    <label className="reservation-add-form__field reservation-add-form__field--table">
                      <span>{t("reservationTable")}</span>
                      <ReservationTablePicker
                        variant="form"
                        value={String(newReservationForm.table || "")}
                        options={newReservationTableOptions}
                        unassigned={!newReservationForm.table}
                        disabled={Boolean(newReservationLockedTableId)}
                        tableColor={defaultTableColor}
                        onChange={(nextTableId) => {
                          if (newReservationLockedTableId) return;
                          const table = tables.find(
                            (item) => String(item._id) === String(nextTableId)
                          );
                          const maxSeats = Math.max(1, Number(table?.seats) || 20);
                          setNewReservationForm((prev) => ({
                            ...prev,
                            table: nextTableId ? String(nextTableId) : "",
                            partySize: Math.min(
                              Number(prev.partySize) || 1,
                              maxSeats
                            ),
                          }));
                        }}
                        ariaLabel={t("reservationTable")}
                      />
                    </label>
                    <label className="reservation-add-form__field">
                      <span>{t("reservationFormSeating")}</span>
                      <select
                        className="form-control"
                        value={newReservationForm.seatingPreference}
                        onChange={(event) =>
                          handleNewReservationChange("seatingPreference", event.target.value)
                        }
                      >
                        <option value="">{t("reservationSeatingAny")}</option>
                        <option value="indoor">{t("reservationSeating_indoor")}</option>
                        <option value="outdoor">{t("reservationSeating_outdoor")}</option>
                        <option value="terrace">{t("reservationSeating_terrace")}</option>
                      </select>
                    </label>
                    </div>
                    <label className="reservation-add-form__field reservation-add-form__field--wide">
                      <span>{t("reservationFormNotes")}</span>
                      <textarea
                        className="form-control reservation-add-form__notes"
                        rows={2}
                        value={newReservationForm.clientNotes}
                        onChange={(event) =>
                          handleNewReservationChange("clientNotes", event.target.value)
                        }
                        placeholder={t("reservationFormNotes")}
                      />
                    </label>
                  </div>
                </section>
              </div>

              <div className="reservation-add-form__actions">
                <button
                  type="button"
                  className="reservation-add-form__reset"
                  disabled={submittingNewReservation}
                  onClick={handleResetNewReservationForm}
                >
                  {t("reservationAddFormReset")}
                </button>
                <button
                  type="submit"
                  className="reservation-add-form__submit"
                  disabled={submittingNewReservation}
                >
                  <i className="fa fa-calendar-plus-o" aria-hidden="true" />
                  {submittingNewReservation ? t("updating") : t("reservationCreateBooking")}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editReservation && editForm && (() => {
        const nameParts = String(editForm.customerName || "")
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        const initials =
          nameParts.length === 0
            ? "?"
            : nameParts.length === 1
              ? nameParts[0].slice(0, 2).toUpperCase()
              : `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();

        return (
          <div
            className="reservation-popup-backdrop"
            onMouseDown={handleCloseEditReservation}
          >
            <div
              className="reservation-popup reservation-edit-popup"
              role="dialog"
              aria-modal="true"
              aria-labelledby="reservation-edit-popup-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="reservation-details-close"
                onClick={handleCloseEditReservation}
                aria-label={t("close")}
              >
                <i className="fa fa-times" />
              </button>

              <div className="reservation-edit-hero">
                <span className="reservation-details-avatar" aria-hidden="true">
                  {initials}
                </span>
                <div className="reservation-details-hero-text">
                  <h4 id="reservation-edit-popup-title">
                    {t("reservationEditBooking")}
                  </h4>
                  <p>{t("reservationEditBookingSubtitle")}</p>
                </div>
              </div>

              <form className="reservation-edit-form" onSubmit={handleSubmitEditReservation}>
                <section className="reservation-edit-section">
                  <div className="reservation-edit-section-head">
                    <span className="reservation-details-section-icon is-contact">
                      <i className="fa fa-user" />
                    </span>
                    <h5>{t("reservationDetailsContact")}</h5>
                  </div>
                  <div className="reservation-edit-grid">
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormFullName")}</span>
                      <input
                        className="form-control"
                        value={editForm.customerName}
                        onChange={(event) =>
                          handleEditFormChange("customerName", event.target.value)
                        }
                        required
                      />
                    </label>
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormPhone")}</span>
                      <input
                        className="form-control"
                        value={editForm.customerPhone}
                        onChange={(event) =>
                          handleEditFormChange("customerPhone", event.target.value)
                        }
                        required
                      />
                    </label>
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormEmail")}</span>
                      <input
                        type="email"
                        className="form-control"
                        value={editForm.customerEmail}
                        onChange={(event) =>
                          handleEditFormChange("customerEmail", event.target.value)
                        }
                      />
                    </label>
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormPreferredContact")}</span>
                      <select
                        className="form-control"
                        value={editForm.preferredContact}
                        onChange={(event) =>
                          handleEditFormChange("preferredContact", event.target.value)
                        }
                      >
                        <option value="phone">{t("reservationContact_phone")}</option>
                        <option value="whatsapp">{t("reservationContact_whatsapp")}</option>
                        <option value="email">{t("reservationContact_email")}</option>
                      </select>
                    </label>
                  </div>
                </section>

                <section className="reservation-edit-section">
                  <div className="reservation-edit-section-head">
                    <span className="reservation-details-section-icon is-booking">
                      <i className="fa fa-calendar" />
                    </span>
                    <h5>{t("reservationDetailsBooking")}</h5>
                  </div>
                  <div className="reservation-edit-grid">
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormDate")}</span>
                      <input
                        type="date"
                        className="form-control"
                        value={editForm.date}
                        onChange={(event) =>
                          handleEditFormChange("date", event.target.value)
                        }
                        required
                      />
                    </label>
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormTime")}</span>
                      <input
                        type="time"
                        className="form-control"
                        value={editForm.time}
                        onChange={(event) =>
                          handleEditFormChange("time", event.target.value)
                        }
                        required
                      />
                    </label>
                    <label className="reservation-edit-field">
                      <span>{t("reservationPlannedDurationLabel")}</span>
                      <select
                        className="form-control"
                        value={editForm.durationMinutes}
                        onChange={(event) =>
                          handleEditFormChange(
                            "durationMinutes",
                            Number(event.target.value)
                          )
                        }
                      >
                        {DURATION_OPTIONS.map((minutes) => (
                          <option key={minutes} value={minutes}>
                            {formatDurationLabel(minutes)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="reservation-edit-field">
                      <span>{t("reservationActualDurationLabel")}</span>
                      <select
                        className="form-control"
                        value={editForm.actualDurationMinutes}
                        onChange={(event) =>
                          handleEditFormChange(
                            "actualDurationMinutes",
                            event.target.value === ""
                              ? ""
                              : Number(event.target.value)
                          )
                        }
                      >
                        <option value="">{t("reservationActualDurationNotSet")}</option>
                        {ACTUAL_DURATION_OPTIONS.map((minutes) => (
                          <option key={minutes} value={minutes}>
                            {formatDurationLabel(minutes)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="reservation-edit-row reservation-edit-row--triple">
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormGuests")}</span>
                      <input
                        type="number"
                        min="1"
                        max={editTableMaxSeats}
                        className="form-control"
                        value={editForm.partySize}
                        onChange={(event) =>
                          handleEditFormChange(
                            "partySize",
                            Math.max(1, Number(event.target.value) || 1)
                          )
                        }
                        required
                      />
                    </label>
                    <label className="reservation-edit-field reservation-edit-field--table">
                      <span>{t("reservationTable")}</span>
                      <ReservationTablePicker
                        variant="form"
                        value={editForm.table}
                        options={getAssignableTableOptions(editReservation, {
                          date: editForm.date,
                          time: editForm.time,
                          durationMinutes: editForm.durationMinutes,
                        })}
                        tableColor={defaultTableColor}
                        onChange={(nextTableId) => {
                          const table = tables.find((item) => item._id === nextTableId);
                          const maxSeats = Math.max(1, Number(table?.seats) || 20);
                          setEditForm((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  table: nextTableId,
                                  partySize: Math.min(prev.partySize, maxSeats),
                                }
                              : prev
                          );
                        }}
                        ariaLabel={t("reservationTable")}
                      />
                    </label>
                    <label className="reservation-edit-field">
                      <span>{t("reservationFormSeating")}</span>
                      <select
                        className="form-control"
                        value={editForm.seatingPreference}
                        onChange={(event) =>
                          handleEditFormChange("seatingPreference", event.target.value)
                        }
                      >
                        <option value="">{t("reservationSeatingAny")}</option>
                        <option value="indoor">{t("reservationSeating_indoor")}</option>
                        <option value="outdoor">{t("reservationSeating_outdoor")}</option>
                        <option value="terrace">{t("reservationSeating_terrace")}</option>
                      </select>
                    </label>
                    </div>
                    <label className="reservation-edit-field">
                      <span>{t("status")}</span>
                      <select
                        className="form-control"
                        value={editForm.status}
                        onChange={(event) =>
                          handleEditFormChange("status", event.target.value)
                        }
                      >
                        {RESERVATION_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {t(`reservationStatus_${status}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </section>

                <section className="reservation-edit-section is-last">
                  <div className="reservation-edit-section-head">
                    <span className="reservation-details-section-icon is-notes">
                      <i className="fa fa-sticky-note" />
                    </span>
                    <h5>{t("notes")}</h5>
                  </div>
                  <label className="reservation-edit-field reservation-edit-field-full">
                    <span>{t("reservationFormNotes")}</span>
                    <textarea
                      className="form-control reservation-edit-notes"
                      rows={3}
                      value={editForm.clientNotes}
                      onChange={(event) =>
                        handleEditFormChange("clientNotes", event.target.value)
                      }
                      placeholder={t("reservationNoNote")}
                    />
                  </label>
                </section>

                <div className="reservation-edit-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCloseEditReservation}
                    disabled={submittingEdit}
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary reservation-edit-save-btn"
                    disabled={submittingEdit}
                  >
                    {submittingEdit ? t("updating") : t("reservationSaveChanges")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      <section className="reservation-plan-shell">
      <div className="reservation-layout-manager card">
        <div className="card-body">
          <div className="reservation-layout-manager__head">
            <div>
              <span>{t("reservationFloorPlan")}</span>
              <h4>{t("reservationFloorSalleManager")}</h4>
            </div>
            <div className="reservation-layout-manager__active">
              <span>{t("reservationFloorPlan")}</span>
              <strong>
                {selectedFloor?.name || "—"} / {selectedSalle?.name || "—"}
              </strong>
            </div>
          </div>

          <div className="reservation-layout-manager__body">
            <section className="reservation-layout-manager__column">
              <div className="reservation-layout-manager__column-head">
                <i className="fas fa-layer-group" aria-hidden="true" />
                <strong>{t("reservationFloor")}</strong>
                <span className="reservation-layout-manager__count">{floors.length}</span>
              </div>

              <div className="reservation-layout-manager__list" role="list">
                {floors.map((floor) => (
                  <div
                    key={floor.id}
                    role="listitem"
                    className={`reservation-layout-chip${
                      selectedFloor?.id === floor.id ? " is-active" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="reservation-layout-chip__select"
                      onClick={() => {
                        setSelectedFloorId(floor.id);
                        setSelectedSalleId(
                          floor.salles?.[0]?.id || DEFAULT_SALLE_ID
                        );
                      }}
                    >
                      {floor.name}
                    </button>
                    <button
                      type="button"
                      className="reservation-layout-chip__delete"
                      onClick={(event) => handleDeleteFloor(event, floor)}
                      title={t("reservationDeleteFloor")}
                      aria-label={t("reservationDeleteFloor")}
                    >
                      <i className="fas fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>

              <form
                className="reservation-layout-manager__add"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleAddFloor();
                }}
              >
                <input
                  type="text"
                  className="form-control"
                  value={newFloorName}
                  placeholder={t("reservationFloorName")}
                  onChange={(event) => setNewFloorName(event.target.value)}
                  aria-label={t("reservationFloorName")}
                />
                <button type="submit" className="reservation-layout-manager__btn">
                  <i className="fa fa-plus" aria-hidden="true" />
                  {t("reservationAddFloor")}
                </button>
              </form>
            </section>

            <div className="reservation-layout-manager__divider" aria-hidden="true" />

            <section className="reservation-layout-manager__column">
              <div className="reservation-layout-manager__column-head">
                <i className="fas fa-door-open" aria-hidden="true" />
                <strong>{t("reservationSalle")}</strong>
                <span className="reservation-layout-manager__count">
                  {selectedFloor?.salles?.length || 0}
                </span>
              </div>

              <div className="reservation-layout-manager__list" role="list">
                {(selectedFloor?.salles || []).map((salle) => (
                  <div
                    key={salle.id}
                    role="listitem"
                    className={`reservation-layout-chip${
                      selectedSalle?.id === salle.id ? " is-active" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="reservation-layout-chip__select"
                      onClick={() => setSelectedSalleId(salle.id)}
                    >
                      {salle.name}
                    </button>
                    <button
                      type="button"
                      className="reservation-layout-chip__delete"
                      onClick={(event) => handleDeleteSalle(event, salle)}
                      title={t("reservationDeleteSalle")}
                      aria-label={t("reservationDeleteSalle")}
                    >
                      <i className="fas fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>

              <form
                className="reservation-layout-manager__add"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleAddSalle();
                }}
              >
                <input
                  type="text"
                  className="form-control"
                  value={newSalleName}
                  placeholder={t("reservationSalleName")}
                  onChange={(event) => setNewSalleName(event.target.value)}
                  aria-label={t("reservationSalleName")}
                  disabled={!selectedFloor}
                />
                <button
                  type="submit"
                  className="reservation-layout-manager__btn"
                  disabled={!selectedFloor}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  {t("reservationAddSalle")}
                </button>
              </form>
            </section>
          </div>
        </div>
      </div>

      <div className="row reservation-plan-workspace">
        <div className="col-xl-12">
          <div className="card reservation-plan-card">
            <div className="card-body reservation-plan-studio">
              <aside className="reservation-plan-toolbox">
                <div className="reservation-plan-toolbox__section reservation-folders-section">
                  <div className="reservation-folders-card">
                    <header className="reservation-folders-section__head">
                      <h5>{t("reservationFoldersSectionTitle")}</h5>
                      <p>{t("reservationFoldersBrowseHint")}</p>
                    </header>

                    <div className="reservation-folders-where">
                      <label className="reservation-folders-field">
                        <span className="reservation-folders-field__label">
                          {t("reservationFloor")}
                        </span>
                        <div className="reservation-folders-select-wrap">
                          <select
                            className="reservation-folders-select"
                            value={folderBrowseFloorId || ""}
                            onChange={(event) =>
                              handleSelectFolderBrowseFloor(event.target.value)
                            }
                            aria-label={t("reservationFloor")}
                          >
                            <option value="">
                              {t("reservationFoldersSelectFloor")}
                            </option>
                            {floors.map((floor, index) => {
                              const labels = resolveFloorSalleNames(
                                `${floor.id}:${floor.salles?.[0]?.id || ""}`
                              );
                              const floorName =
                                floor.name &&
                                !looksLikeEntityId(floor.name, "floor")
                                  ? floor.name
                                  : labels.floorName ||
                                    `${t("reservationFloor")} ${index + 1}`;
                              return (
                                <option key={floor.id} value={floor.id}>
                                  {floorName}
                                </option>
                              );
                            })}
                          </select>
                          <i
                            className="fas fa-chevron-down"
                            aria-hidden="true"
                          />
                        </div>
                      </label>

                      <label className="reservation-folders-field">
                        <span className="reservation-folders-field__label">
                          {t("reservationSalle")}
                        </span>
                        <div className="reservation-folders-select-wrap">
                          <select
                            className="reservation-folders-select"
                            value={folderBrowseSalleId || ""}
                            onChange={(event) =>
                              handleSelectFolderBrowseSalle(
                                event.target.value
                              )
                            }
                            aria-label={t("reservationSalle")}
                            disabled={!folderBrowseFloorId}
                          >
                            <option value="">
                              {t("reservationFoldersSelectRoom")}
                            </option>
                            {folderBrowseFloorId && folderBrowseSalles.length === 0 ? (
                              <option value="" disabled>
                                {t("reservationFoldersNoSalles")}
                              </option>
                            ) : null}
                            {folderBrowseSalles.map((salle, index) => {
                              const salleName =
                                salle.name &&
                                !looksLikeEntityId(salle.name, "salle")
                                  ? salle.name
                                  : `${t("reservationSalle")} ${index + 1}`;
                              return (
                                <option key={salle.id} value={salle.id}>
                                  {salleName}
                                </option>
                              );
                            })}
                          </select>
                          <i
                            className="fas fa-chevron-down"
                            aria-hidden="true"
                          />
                        </div>
                      </label>
                    </div>

                    {folderBrowseFloorId && folderBrowseSalleId ? (
                    <div className="reservation-folders-list-wrap">
                      <div className="reservation-folders-list-head">
                        <div className="reservation-folders-list-head__meta">
                          <strong>{t("reservationFoldersTitle")}</strong>
                          <span>
                            {folderBrowseLabels.floorName} ·{" "}
                            {folderBrowseLabels.salleName}
                          </span>
                        </div>
                        <div className="reservation-folders-list-head__actions">
                          <em>
                            {folderBrowseFolders.length}/{MAX_FOLDERS_PER_SALLE}
                          </em>
                          <button
                            type="button"
                            className="reservation-folder-create"
                            title={t("reservationFolderCreateHint")}
                            onClick={handleCreateFolder}
                            disabled={
                              savingLayout ||
                              folderBrowseFolders.length >= MAX_FOLDERS_PER_SALLE
                            }
                          >
                            <i className="fas fa-plus" aria-hidden="true" />
                            <span>{t("reservationFolderCreateShort")}</span>
                          </button>
                        </div>
                      </div>

                      <div className="reservation-folders-list" role="list">
                        {folderBrowseFolders.length === 0 ? (
                          <div className="reservation-folders-empty">
                            <strong>{t("reservationFoldersEmptyInSalle")}</strong>
                            <span>{t("reservationFolderCreateHint")}</span>
                          </div>
                        ) : (
                          folderBrowseFolders.map((folder) => {
                            const isActive = activeFolderId === folder.id;
                            const folderLabel =
                              folder.name || t("reservationFolder");
                            const hasSavedVersion = Boolean(
                              folder.tables?.length
                            );
                            return (
                              <div
                                key={folder.id}
                                role="listitem"
                                className={`reservation-folder-row${
                                  isActive ? " is-active" : ""
                                }${hasSavedVersion ? "" : " is-empty"}`}
                              >
                                <button
                                  type="button"
                                  className="reservation-folder-row__main"
                                  title={
                                    isActive
                                      ? t("reservationFolderDeselectHint", {
                                          name: folderLabel,
                                        })
                                      : hasSavedVersion
                                        ? t("reservationFolderOpenSaved", {
                                            name: folderLabel,
                                          })
                                        : t("reservationFolderOpenEmpty", {
                                            name: folderLabel,
                                          })
                                  }
                                  onClick={() =>
                                    handleOpenFolder(
                                      folder,
                                      folderBrowseLayoutKey
                                    )
                                  }
                                  disabled={savingLayout}
                                >
                                  <span className="reservation-folder-row__text">
                                    <strong>{folderLabel}</strong>
                                    <span
                                      className={`reservation-folder-row__status${
                                        hasSavedVersion
                                          ? " is-saved"
                                          : " is-draft"
                                      }`}
                                    >
                                      {hasSavedVersion
                                        ? t("reservationFolderSavedBadge")
                                        : t("reservationFolderEmptyBadge")}
                                    </span>
                                  </span>
                                </button>
                                <div className="reservation-folder-row__actions">
                                  <button
                                    type="button"
                                    className="reservation-folder-row__action is-affect"
                                    title={t("reservationAffectToSetupHint", {
                                      name: folderLabel,
                                    })}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void handleAffectDesignToSetup(folder);
                                    }}
                                    disabled={
                                      savingLayout ||
                                      folderBrowseLayoutKey !==
                                        selectedLayoutKey ||
                                      (displayTables.length === 0 &&
                                        floorElements.length === 0)
                                    }
                                  >
                                    <i
                                      className="fas fa-link"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="reservation-folder-row__action"
                                    title={t("reservationFolderRenameTitle")}
                                    onClick={(event) =>
                                      handleRenameFolder(event, folder)
                                    }
                                    disabled={savingLayout}
                                  >
                                    <i
                                      className="fas fa-pen"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="reservation-folder-row__action is-archive"
                                    title={t("reservationFolderArchive")}
                                    onClick={(event) =>
                                      handleArchiveFolder(event, folder.id)
                                    }
                                    disabled={savingLayout}
                                  >
                                    <i
                                      className="fas fa-archive"
                                      aria-hidden="true"
                                    />
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                    ) : (
                      <div className="reservation-folders-empty is-soft">
                        <strong>
                          {folderBrowseFloorId
                            ? t("reservationFoldersSelectRoom")
                            : t("reservationFoldersSelectFloor")}
                        </strong>
                        <span>{t("reservationFoldersPickFloorSalle")}</span>
                      </div>
                    )}

                    {activeFolder ? (
                      <div className="reservation-folders-active-note">
                        <div className="reservation-folders-active-note__label">
                          <span className="reservation-folders-active-note__pulse" />
                          <div>
                            <span>{t("reservationFolderActiveLabel")}</span>
                            <strong>{activeFolder.name}</strong>
                          </div>
                        </div>
                        <p>
                          {activeFolder.tables?.length
                            ? t("reservationFolderHint")
                            : t("reservationFolderSaveFirstHint")}
                        </p>
                        {unplacedTables.length > 0 ? (
                          <>
                            <p className="reservation-disposition-folder-panel__tray-label">
                              {t("reservationFolderUnplacedHint")}
                            </p>
                            <div className="reservation-disposition-unplaced-tray">
                              {unplacedTables.map((table) => (
                                <button
                                  key={table._id}
                                  type="button"
                                  className="reservation-disposition-unplaced-chip"
                                  title={t("reservationFolderPlaceTable")}
                                  onClick={() =>
                                    handlePlaceUnplacedTable(table)
                                  }
                                >
                                  <span>
                                    {table.name || `#${table.tableNumber}`}
                                  </span>
                                  <i
                                    className="fas fa-plus"
                                    aria-hidden="true"
                                  />
                                </button>
                              ))}
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="reservation-plan-toolbox__section">
                  <h5>{t("reservationTableExamples")}</h5>
                  <p>{t("reservationTableExamplesHint")}</p>
                  <div className="reservation-toolbox-shapes">
                    {tableShapeOptions.map((option, index) => (
                      <button
                        key={option.value}
                        type="button"
                        className="reservation-toolbox-shape"
                        disabled={submittingTable}
                        onClick={() => handleQuickAddTable(option, index)}
                        title={`${t(option.labelKey)} · ${option.seats} ${t("reservationSeats")}`}
                      >
                        <TableShapePreview
                          shape={option.value}
                          seats={option.seats}
                          baseColor={defaultTableColor}
                        />
                        <span className="reservation-toolbox-shape__label">
                          {t(option.labelKey)}
                        </span>
                        <span className="reservation-toolbox-shape__seats">
                          {option.seats}p
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="reservation-plan-toolbox__section">
                  <h5>{t("reservationFloorElements")}</h5>
                  <p>{t("reservationFloorElementsHint")}</p>
                  <div className="reservation-toolbox-elements">
                    {floorElementOptions.map((option, index) => (
                      <button
                        key={option.type}
                        type="button"
                        className={`reservation-toolbox-element is-${option.type}`}
                        onClick={() => handleQuickAddFloorElement(option, index)}
                      >
                        <i className={option.icon} aria-hidden="true" />
                        <span>{t(option.labelKey)}</span>
                      </button>
                    ))}
                  </div>

                  <div className="reservation-toolbox-availability">
                    <h6>{t("reservationPlanAvailabilityTitle")}</h6>
                    <p>{t("reservationPlanAvailabilityHint")}</p>
                    <div className="reservation-toolbox-availability__grid">
                      <label className="reservation-toolbox-availability__field">
                        <span>{t("reservationDate")}</span>
                        <input
                          type="date"
                          className="form-control"
                          value={filters.date}
                          onChange={(event) =>
                            handleFilterChange("date", event.target.value)
                          }
                        />
                      </label>
                      <label className="reservation-toolbox-availability__field">
                        <span>{t("reservationTime")}</span>
                        <input
                          type="time"
                          className="form-control"
                          value={filters.time}
                          onChange={(event) =>
                            handleFilterChange("time", event.target.value)
                          }
                        />
                      </label>
                    </div>
                    <div className="reservation-toolbox-availability__summary">
                      <span className="is-available">
                        {availableTablesCount} {t("reservationAvailable")}
                      </span>
                      <span className="is-booked">
                        {reservationStats.booked} {t("reservationBooked")}
                      </span>
                    </div>
                  </div>
                </div>
              </aside>

              <div className="reservation-plan-stage">
                <div className="reservation-plan-stage__head">
                  <div className="reservation-legend reservation-legend--inline">
                    <span className="is-available">{t("reservationAvailable")}</span>
                    <span className="is-booked">{t("reservationBooked")}</span>
                    <span className="is-small">{t("reservationTooSmall")}</span>
                  </div>
                  <p className="reservation-plan-stage__hint">
                    <i className="fas fa-arrows-alt" aria-hidden="true" />
                    {t("reservationDragHint")}
                  </p>
                </div>

              <div
                className={`reservation-floor ${isPlanFullscreen ? "is-fullscreen" : ""}`}
                ref={floorRef}
              >
                <div className="reservation-plan-current">
                  <span>{t("reservationFloorPlan")}</span>
                  <strong>
                    {selectedFloor?.name} / {selectedSalle?.name}
                  </strong>
                </div>
                {activeFolder ? (
                  <div className="reservation-folder-status">
                    <i className="fas fa-folder-open" aria-hidden="true" />
                    <div>
                      <span>{t("reservationFolderActiveLabel")}</span>
                      <strong>{activeFolder.name}</strong>
                      <small>
                        {(() => {
                          const labels = resolveFloorSalleNames(
                            selectedLayoutKey,
                            activeFolder
                          );
                          return `${labels.floorName} / ${labels.salleName}`;
                        })()}
                      </small>
                    </div>
                  </div>
                ) : null}
                <div
                  className="reservation-plan-switcher"
                  onPointerDown={(event) => event.stopPropagation()}
                  onPointerUp={(event) => event.stopPropagation()}
                >
                  <div className="reservation-plan-icon-row">
                    <button
                      type="button"
                      className="reservation-plan-icon-btn"
                      aria-label={`${t("reservationFloor")} previous`}
                      onClick={() => handleStepFloor(-1)}
                    >
                      <i className="fas fa-chevron-left" />
                    </button>
                    <span className="reservation-plan-switcher-icon">
                      <i className="fas fa-layer-group" />
                    </span>
                    <button
                      type="button"
                      className="reservation-plan-icon-btn"
                      aria-label={`${t("reservationFloor")} next`}
                      onClick={() => handleStepFloor(1)}
                    >
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                  <div className="reservation-plan-icon-row">
                    <button
                      type="button"
                      className="reservation-plan-icon-btn"
                      aria-label={`${t("reservationSalle")} previous`}
                      onClick={() => handleStepSalle(-1)}
                    >
                      <i className="fas fa-chevron-left" />
                    </button>
                    <span className="reservation-plan-switcher-icon">
                      <i className="fas fa-door-open" />
                    </span>
                    <button
                      type="button"
                      className="reservation-plan-icon-btn"
                      aria-label={`${t("reservationSalle")} next`}
                      onClick={() => handleStepSalle(1)}
                    >
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="reservation-plan-fullscreen-btn"
                  title={
                    isPlanFullscreen
                      ? t("reservationExitFullscreen")
                      : t("reservationOpenFullscreen")
                  }
                  aria-label={
                    isPlanFullscreen
                      ? t("reservationExitFullscreen")
                      : t("reservationOpenFullscreen")
                  }
                  onClick={() => setIsPlanFullscreen((prev) => !prev)}
                  onPointerDown={(event) => event.stopPropagation()}
                  onPointerUp={(event) => event.stopPropagation()}
                >
                  <i className={isPlanFullscreen ? "fas fa-compress" : "fas fa-expand"} />
                </button>
                <div
                  className="reservation-disposition-bar"
                  onPointerDown={(event) => event.stopPropagation()}
                  onPointerUp={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className={`reservation-disposition-default-save${
                      defaultLayoutSnapshot ? " has-default" : ""
                    }`}
                    title={
                      defaultLayoutSnapshot
                        ? t("reservationUpdateDefaultDisposition")
                        : t("reservationSaveAsDefaultDisposition")
                    }
                    onClick={handleSaveAsDefaultDisposition}
                    disabled={savingLayout || displayTables.length === 0}
                  >
                    <i className="fas fa-star" />
                    <span>
                      {defaultLayoutSnapshot
                        ? t("reservationUpdateDefaultShort")
                        : t("reservationSaveDefaultShort")}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`reservation-disposition-default-apply${
                      isOnDefaultLayout ? " is-active" : ""
                    }`}
                    title={t("reservationApplyDefaultDisposition")}
                    onClick={handleApplyDefaultDisposition}
                    disabled={savingLayout || !defaultLayoutSnapshot?.tables?.length}
                  >
                    <i className="fas fa-home" />
                    <span>{t("reservationDefaultDispositionShort")}</span>
                  </button>
                  <span className="reservation-disposition-divider" aria-hidden="true" />
                  <button
                    type="button"
                    className="reservation-disposition-save"
                    title={t("reservationSaveDisposition")}
                    onClick={handleSaveDisposition}
                    disabled={savingLayout || displayTables.length === 0}
                  >
                    <i className="fas fa-save" />
                    <span>
                      {currentDispositions.length}/{MAX_DISPOSITIONS_PER_SALLE}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="reservation-disposition-affect"
                    title={t("reservationAffectHint")}
                    onClick={() => handleAffectDesignToSetup()}
                    disabled={
                      savingLayout ||
                      (displayTables.length === 0 && floorElements.length === 0)
                    }
                  >
                    <i className="fas fa-link" />
                    <span>{t("reservationAffectAction")}</span>
                  </button>
                  {currentDispositions.map((disposition, index) => {
                    const isActive =
                      activeDispositionId === disposition.id &&
                      !activeFolderId &&
                      !layoutDirty;
                    return (
                      <button
                        key={disposition.id}
                        type="button"
                        className={`reservation-disposition-slot${
                          isActive ? " is-active" : ""
                        }`}
                        title={disposition.name || `${t("reservationDisposition")} ${index + 1}`}
                        onClick={() => handleApplyDisposition(disposition)}
                        disabled={savingLayout}
                      >
                        <span>{index + 1}</span>
                        <i
                          className="fa fa-times reservation-disposition-delete"
                          title={t("reservationDeleteDisposition")}
                          onClick={(event) =>
                            handleDeleteDisposition(event, disposition.id)
                          }
                        />
                      </button>
                    );
                  })}
                </div>
                <div className="reservation-floor-room">
                <div className="reservation-floor-walls" aria-hidden="true" />
                <div className="reservation-floor-grid" />
                <div
                  className="reservation-door"
                  style={{
                    left: `${doorPosition.x}%`,
                    top: `${doorPosition.y}%`,
                  }}
                  role="button"
                  tabIndex={0}
                  onPointerDown={handleDoorPointerDown}
                >
                  <span className="reservation-door__icon" aria-hidden="true">
                    <i className="fas fa-door-open" />
                  </span>
                  <span className="reservation-door__label">{t("reservationDoor")}</span>
                </div>
                {floorElements.map((element) => {
                  const option = floorElementOptions.find(
                    (item) => item.type === element.type
                  );
                  return (
                    <div
                      key={element.id}
                      role="button"
                      tabIndex={0}
                      className={`reservation-floor-element is-${element.type}`}
                      style={{
                        left: `${element.position?.x || 50}%`,
                        top: `${element.position?.y || 50}%`,
                        transform: `translate(-50%, -50%) rotate(${
                          element.rotation || 0
                        }deg)`,
                      }}
                      onPointerDown={(event) =>
                        handleFloorElementPointerDown(event, element)
                      }
                    >
                      <button
                        type="button"
                        className="reservation-floor-element-delete"
                        title={t("delete")}
                        onClick={(event) => handleDeleteFloorElement(event, element.id)}
                        onPointerDown={(event) => event.stopPropagation()}
                        onPointerUp={(event) => event.stopPropagation()}
                      >
                        <i className="fa fa-times" />
                      </button>
                      <button
                        type="button"
                        className="reservation-floor-element-rotate"
                        title={t("reservationToggleOrientation")}
                        onClick={(event) => handleRotateFloorElement(event, element.id)}
                        onPointerDown={(event) => event.stopPropagation()}
                        onPointerUp={(event) => event.stopPropagation()}
                      >
                        <i className="fas fa-sync-alt" />
                      </button>
                      <i className={option?.icon || "fas fa-square"} />
                      <span>{t(option?.labelKey || "reservationFloorElement")}</span>
                    </div>
                  );
                })}
                {loading ? (
                  <div className="reservation-empty-state">{t("loading")}...</div>
                ) : displayTables.length === 0 && floorElements.length === 0 ? (
                  <div className="reservation-empty-state">
                    <i className={activeFolder ? "fas fa-folder-open" : "fas fa-chair"} />
                    <strong>
                      {activeFolder
                        ? t("reservationFolderEmptyTitle", {
                            name: activeFolder.name,
                          })
                        : t("reservationNoTables")}
                    </strong>
                    <span>
                      {activeFolder
                        ? t("reservationFolderEmptyHint")
                        : t("reservationNoTablesHint")}
                    </span>
                  </div>
                ) : (
                  displayTables.map((table) => {
                    const tableLocked = isTableLockedForSlot(table);
                    return (
                    <div
                      key={table._id}
                      role="button"
                      tabIndex={0}
                      className={`reservation-table reservation-table-${table.shape} is-${table.availability} ${
                        selectedTableId === table._id ? "is-selected" : ""
                      }${tableLocked ? " is-locked" : ""}`}
                      style={{
                        left: `${table.position?.x || 50}%`,
                        top: `${table.position?.y || 50}%`,
                        width: `${(table.size?.width || 88) * TABLE_RENDER_SCALE}px`,
                        height: `${(table.size?.height || 88) * TABLE_RENDER_SCALE}px`,
                        transform: `translate(-50%, -50%) rotate(${table.rotation || 0}deg)`,
                      }}
                      onPointerDown={(event) => handlePointerDown(event, table)}
                      onPointerUp={() => handleTablePointerUp(table)}
                      onPointerCancel={handleWindowPointerUp}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handleOpenTablePopup(table);
                        }
                      }}
                    >
                      {!tableLocked ? (
                        <>
                      <button
                        type="button"
                        className="reservation-table-orientation-btn"
                        title={t("reservationToggleOrientation")}
                        onClick={(event) => handleRotateTable(event, table._id)}
                        onPointerDown={(event) => event.stopPropagation()}
                        onPointerUp={(event) => event.stopPropagation()}
                      >
                        <i className="fas fa-sync-alt" />
                      </button>
                      <button
                        type="button"
                        className="reservation-table-delete-btn"
                        title={t("delete")}
                        disabled={submittingTable}
                        onClick={(event) => handleDeleteTable(event, table._id)}
                        onPointerDown={(event) => event.stopPropagation()}
                        onPointerUp={(event) => event.stopPropagation()}
                      >
                        <i className="fa fa-trash" />
                      </button>
                      {selectedTableId === table._id
                        ? TABLE_RESIZE_HANDLES.map((handle) => (
                            <button
                              key={handle.key}
                              type="button"
                              className={`reservation-table-resize-handle is-${handle.key}`}
                              style={{
                                cursor: getTableResizeCursor(
                                  handle.key,
                                  table.rotation || 0
                                ),
                              }}
                              title={t("reservationResizeTable")}
                              aria-label={t("reservationResizeTable")}
                              onPointerDown={(event) =>
                                handleResizePointerDown(event, table, handle)
                              }
                              onPointerUp={(event) => event.stopPropagation()}
                              onClick={(event) => event.stopPropagation()}
                            />
                          ))
                        : null}
                        </>
                      ) : null}
                      <TableShapeIllustration
                        shape={table.shape}
                        seats={table.seats}
                        status={table.availability}
                        baseColor={resolveTableColor(table)}
                        className="reservation-table-illustration--floor"
                      />
                      <div className="reservation-table-info">
                        <strong>{table.name}</strong>
                        <span>
                          {table.seats} {t("reservationSeats")}
                        </span>
                      </div>
                      <span
                        className={`reservation-table-badge is-${table.availability}`}
                        title={t(`reservationAvailability_${table.availability}`)}
                      >
                        {t(`reservationAvailability_${table.availability}`)}
                      </span>
                    </div>
                    );
                  })
                )}
                </div>
              </div>
              <div className="reservation-plan-save-bar is-bottom">
                <div>
                  <strong>
                    {activeFolder
                      ? t("reservationFolderSaveTitle", {
                          name: activeFolder.name,
                        })
                      : t("reservationDragSaveTitle")}
                  </strong>
                  <span>
                    {activeFolder
                      ? !salleTables.length
                        ? t("reservationFolderAddTablesFirst")
                        : layoutDirty || folderNeedsFirstSave
                          ? t("reservationFolderSavePending")
                          : t("reservationNoUnsavedChanges")
                      : layoutDirty
                        ? t("reservationUnsavedChanges")
                        : t("reservationNoUnsavedChanges")}
                  </span>
                </div>
                <button
                  className={`reservation-plan-save-btn${
                    activeFolder ? " is-folder-save" : ""
                  }`}
                  type="button"
                  disabled={savingLayout || !canSaveLayout}
                  onClick={handleSaveLayout}
                >
                  <span className="reservation-plan-save-btn__label">
                    {savingLayout
                      ? t("updating")
                      : activeFolder
                        ? t("reservationSaveFolder")
                        : t("reservationSavePositionChanges")}
                  </span>
                  <span className="reservation-plan-save-btn__plus" aria-hidden="true">
                    <i className={`fas ${activeFolder ? "fa-folder" : "fa-plus"}`} />
                  </span>
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </section>

      {affectPicker ? (
        <div
          className="reservation-affect-backdrop"
          onMouseDown={closeAffectPicker}
        >
          <div
            className="reservation-affect-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reservation-affect-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="reservation-affect-modal__close"
              onClick={closeAffectPicker}
              aria-label={t("close")}
            >
              ×
            </button>

            <div className="reservation-affect-modal__head">
              <h3 id="reservation-affect-title">{t("reservationAffectTitle")}</h3>
              <p>
                {affectPicker.step === 0
                  ? t("reservationAffectChoiceHint")
                  : t("reservationAffectStepHint")}
              </p>
            </div>

            {affectPicker.step > 0 ? (
              <ol className="reservation-affect-steps" aria-label={t("reservationAffectTitle")}>
                {[
                  { step: 1, label: t("reservationAffectStepFloor") },
                  { step: 2, label: t("reservationAffectStepPlan") },
                  { step: 3, label: t("reservationAffectStepSetup") },
                ].map((item) => (
                  <li
                    key={item.step}
                    className={`reservation-affect-steps__item${
                      affectPicker.step === item.step ? " is-active" : ""
                    }${affectPicker.step > item.step ? " is-done" : ""}`}
                  >
                    <span>{item.step}</span>
                    <strong>{item.label}</strong>
                  </li>
                ))}
              </ol>
            ) : null}

            <div className="reservation-affect-modal__body">
              {affectPicker.step === 0 ? (
                <>
                  <h4>{t("reservationAffectChooseOption")}</h4>
                  <div className="reservation-affect-choice-list" role="list">
                    <button
                      type="button"
                      role="listitem"
                      className="reservation-affect-choice is-current-setup"
                      onClick={saveToCurrentSetup}
                      disabled={savingLayout}
                    >
                      <span className="reservation-affect-choice__icon">
                        <i className="fas fa-folder-open" aria-hidden="true" />
                      </span>
                      <span className="reservation-affect-choice__text">
                        <strong>
                          {t("reservationAffectSaveCurrent", {
                            name: activeFolder?.name || t("reservationFolder"),
                          })}
                        </strong>
                        <small>
                          {t("reservationAffectSaveCurrentHint")}
                        </small>
                      </span>
                      <i className="fas fa-check" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      role="listitem"
                      className="reservation-affect-choice"
                      onClick={openAffectPickerChooseOther}
                    >
                      <span className="reservation-affect-choice__icon">
                        <i className="fas fa-folder-plus" aria-hidden="true" />
                      </span>
                      <span className="reservation-affect-choice__text">
                        <strong>{t("reservationAffectChooseOther")}</strong>
                        <small>{t("reservationAffectChooseOtherHint")}</small>
                      </span>
                      <i className="fas fa-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                </>
              ) : null}

              {affectPicker.step === 1 ? (
                <>
                  <h4>{t("reservationAffectChooseFloor")}</h4>
                  <div className="reservation-affect-choice-list" role="list">
                    {floors.map((floor, index) => {
                      const floorName =
                        floor.name && !looksLikeEntityId(floor.name, "floor")
                          ? floor.name
                          : `${t("reservationFloor")} ${index + 1}`;
                      const setupCount = (floor.salles || []).reduce(
                        (total, salle) =>
                          total +
                          (planFolders[`${floor.id}:${salle.id}`]?.length || 0),
                        0
                      );
                      return (
                        <button
                          key={floor.id}
                          type="button"
                          role="listitem"
                          className={`reservation-affect-choice${
                            affectPicker.floorId === floor.id ? " is-selected" : ""
                          }`}
                          onClick={() =>
                            setAffectPicker((prev) => ({
                              ...prev,
                              step: 2,
                              floorId: floor.id,
                              salleId: "",
                              setupId: "",
                            }))
                          }
                        >
                          <span className="reservation-affect-choice__icon">
                            <i className="fas fa-layer-group" aria-hidden="true" />
                          </span>
                          <span className="reservation-affect-choice__text">
                            <strong>{floorName}</strong>
                            <small>
                              {t("reservationAffectFloorMeta", {
                                rooms: floor.salles?.length || 0,
                                setups: setupCount,
                              })}
                            </small>
                          </span>
                          <i className="fas fa-chevron-right" aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : null}

              {affectPicker.step === 2 ? (
                <>
                  <h4>{t("reservationAffectChoosePlan")}</h4>
                  <p className="reservation-affect-path">
                    {affectPickerFloor?.name &&
                    !looksLikeEntityId(affectPickerFloor.name, "floor")
                      ? affectPickerFloor.name
                      : t("reservationFloor")}
                  </p>
                  <div className="reservation-affect-choice-list" role="list">
                    {affectPickerRooms.length === 0 ? (
                      <div className="reservation-affect-empty">
                        {t("reservationFoldersNoSalles")}
                      </div>
                    ) : (
                      affectPickerRooms.map((salle, index) => {
                        const salleName =
                          salle.name && !looksLikeEntityId(salle.name, "salle")
                            ? salle.name
                            : `${t("reservationSalle")} ${index + 1}`;
                        const layoutKey = `${affectPicker.floorId}:${salle.id}`;
                        const setupCount =
                          planFolders[layoutKey]?.length || 0;
                        const isCurrent = layoutKey === selectedLayoutKey;
                        return (
                          <button
                            key={salle.id}
                            type="button"
                            role="listitem"
                            className={`reservation-affect-choice${
                              affectPicker.salleId === salle.id
                                ? " is-selected"
                                : ""
                            }`}
                            onClick={() =>
                              setAffectPicker((prev) => ({
                                ...prev,
                                step: 3,
                                salleId: salle.id,
                                setupId: "",
                              }))
                            }
                          >
                            <span className="reservation-affect-choice__icon">
                              <i className="fas fa-door-open" aria-hidden="true" />
                            </span>
                            <span className="reservation-affect-choice__text">
                              <strong>{salleName}</strong>
                              <small>
                                {t("reservationAffectPlanMeta", {
                                  setups: setupCount,
                                })}
                                {isCurrent
                                  ? ` · ${t("reservationAffectCurrentRoom")}`
                                  : ""}
                              </small>
                            </span>
                            <i
                              className="fas fa-chevron-right"
                              aria-hidden="true"
                            />
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              ) : null}

              {affectPicker.step === 3 ? (
                <>
                  <h4>{t("reservationAffectChooseSetup")}</h4>
                  <p className="reservation-affect-path">
                    {affectPickerLabels.floorName} / {affectPickerLabels.salleName}
                    {affectPickerLayoutKey === selectedLayoutKey
                      ? ` · ${t("reservationAffectCurrentRoom")}`
                      : ` · ${t("reservationAffectOtherRoom")}`}
                  </p>
                  <div className="reservation-affect-choice-list" role="list">
                    <button
                      type="button"
                      role="listitem"
                      className="reservation-affect-choice is-create"
                      disabled={
                        affectPickerSetups.length >= MAX_FOLDERS_PER_SALLE ||
                        savingLayout
                      }
                      title={
                        affectPickerSetups.length >= MAX_FOLDERS_PER_SALLE
                          ? t("reservationFolderLimit")
                          : t("reservationAffectCreateNewHint")
                      }
                      onClick={handleCreateSetupFromAffectPicker}
                    >
                      <span className="reservation-affect-choice__icon">
                        <i className="fas fa-plus" aria-hidden="true" />
                      </span>
                      <span className="reservation-affect-choice__text">
                        <strong>{t("reservationAffectCreateNew")}</strong>
                        <small>{t("reservationAffectCreateNewHint")}</small>
                      </span>
                      <i className="fas fa-chevron-right" aria-hidden="true" />
                    </button>
                    {affectPickerSetups.length === 0 ? (
                      <div className="reservation-affect-empty">
                        {t("reservationAffectEmptyInRoom")}
                      </div>
                    ) : (
                      affectPickerSetups.map((setup) => {
                        const hasSaved = Boolean(setup.tables?.length);
                        return (
                          <button
                            key={setup.id}
                            type="button"
                            role="listitem"
                            className={`reservation-affect-choice is-setup${
                              affectPicker.setupId === setup.id
                                ? " is-selected"
                                : ""
                            }`}
                            onClick={() =>
                              setAffectPicker((prev) => ({
                                ...prev,
                                setupId: setup.id,
                              }))
                            }
                          >
                            <span className="reservation-affect-choice__icon">
                              <i
                                className={`fas ${
                                  hasSaved ? "fa-folder-open" : "fa-folder"
                                }`}
                                aria-hidden="true"
                              />
                            </span>
                            <span className="reservation-affect-choice__text">
                              <strong>
                                {setup.name || t("reservationFolder")}
                              </strong>
                              <small>
                                {affectPickerLabels.floorName} /{" "}
                                {affectPickerLabels.salleName}
                              </small>
                            </span>
                            <span
                              className={`reservation-affect-setup__badge ${
                                hasSaved ? "is-saved" : "is-draft"
                              }`}
                            >
                              {hasSaved
                                ? t("reservationFolderSavedBadge")
                                : t("reservationFolderEmptyBadge")}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              ) : null}
            </div>

            <div className="reservation-affect-modal__footer">
              <button
                type="button"
                className="reservation-affect-btn is-ghost"
                onClick={() => {
                  if (affectPicker.step <= 0) {
                    closeAffectPicker();
                    return;
                  }
                  if (affectPicker.step === 1) {
                    if (affectPicker.hasCurrent && activeFolder) {
                      setAffectPicker((prev) => ({
                        ...prev,
                        step: 0,
                        salleId: selectedSalle?.id || "",
                        setupId: activeFolder.id,
                      }));
                      return;
                    }
                    closeAffectPicker();
                    return;
                  }
                  setAffectPicker((prev) => ({
                    ...prev,
                    step: prev.step - 1,
                    ...(prev.step === 2
                      ? { salleId: "", setupId: "" }
                      : { setupId: "" }),
                  }));
                }}
              >
                {affectPicker.step === 0 ||
                (affectPicker.step === 1 && !affectPicker.hasCurrent)
                  ? t("cancel") || "Cancel"
                  : t("reservationAffectBack")}
              </button>
              {affectPicker.step === 0 ? (
                <button
                  type="button"
                  className="reservation-affect-btn is-primary"
                  disabled={savingLayout || !activeFolder}
                  onClick={saveToCurrentSetup}
                >
                  {t("reservationAffectSaveCurrentShort")}
                </button>
              ) : null}
              {affectPicker.step === 3 ? (
                <button
                  type="button"
                  className="reservation-affect-btn is-primary"
                  disabled={
                    !affectPicker.setupId ||
                    affectPickerLayoutKey !== selectedLayoutKey ||
                    savingLayout
                  }
                  title={
                    affectPickerLayoutKey !== selectedLayoutKey
                      ? t("reservationAffectOtherRoom")
                      : t("reservationAffectHint")
                  }
                  onClick={confirmAffectPicker}
                >
                  {t("reservationAffectAction")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {popupTable && (
        <div className="reservation-table-popup-backdrop" onMouseDown={handleCloseTablePopup}>
          <div
            className="reservation-table-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reservation-table-popup-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="reservation-table-popup__close"
              onClick={handleCloseTablePopup}
              aria-label={t("close")}
            >
              ×
            </button>

            <div className="reservation-table-popup__hero">
              <span
                className={`reservation-table-popup__status is-${popupTable.availability}`}
              >
                {t(`reservationAvailability_${popupTable.availability}`)}
              </span>
              <h3 id="reservation-table-popup-title">{popupTable.name}</h3>
              <p>{t("reservationTableDetails")}</p>
              {isTableLockedForSlot(popupTable) && popupTable.bookedReservation ? (
                <p className="reservation-table-popup__locked-note">
                  {t("reservationTableLockedHint")} —{" "}
                  <strong>{popupTable.bookedReservation.customerName}</strong>
                </p>
              ) : null}
            </div>

            <div className="reservation-table-popup__stats">
              <div className="reservation-table-popup__stat">
                <span>{t("reservationSeats")}</span>
                <strong>{popupTable.seats}</strong>
              </div>
              <div className="reservation-table-popup__stat">
                <span>{t("status")}</span>
                <strong>{t(`reservationAvailability_${popupTable.availability}`)}</strong>
              </div>
              <div className="reservation-table-popup__stat">
                <span>{t("reservationShape")}</span>
                <strong>{t(getTableShapeLabelKey(popupTable.shape))}</strong>
              </div>
            </div>

            <section className="reservation-table-popup__section">
              <h4>{t("reservationEditTable")}</h4>

              <div className="reservation-table-popup__color is-prominent">
                <div className="reservation-table-popup__color-head">
                  <span>{t("colorPalette")}</span>
                  <span className="reservation-table-popup__color-hint">
                    {t("reservationTableColorHint")}
                  </span>
                </div>
                <div className="reservation-table-popup__color-body">
                  <div className="reservation-table-popup__color-palette-wrap">
                    <div
                      className="reservation-table-popup__color-palette"
                      role="listbox"
                      aria-label={t("colorPalette")}
                    >
                      {TABLE_COLOR_PRESETS.map((preset) => {
                        const activeColor = normalizeTableHex(
                          popupColor || defaultTableColor
                        );
                        const selected = activeColor === preset;
                        const isCustom =
                          Boolean(popupColor) &&
                          activeColor === preset;
                        return (
                          <button
                            key={preset}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`reservation-table-popup__color-swatch${
                              selected ? " is-selected" : ""
                            }${isCustom ? " is-custom" : ""}`}
                            style={{ backgroundColor: preset }}
                            title={preset}
                            disabled={isTableLockedForSlot(popupTable)}
                            onClick={() => setPopupColor(preset)}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div
                    className="reservation-table-popup__color-preview"
                    aria-hidden
                  >
                    <TableShapeIllustration
                      shape={popupTable.shape}
                      seats={popupSeats}
                      status="available"
                      baseColor={normalizeTableHex(
                        popupColor || defaultTableColor
                      )}
                    />
                  </div>
                </div>
                <div className="reservation-table-popup__color-row">
                  <span className="reservation-table-popup__color-palette-label">
                    {t("colorPalette")}
                  </span>
                  <input
                    type="color"
                    className="reservation-table-popup__color-picker"
                    aria-label={t("reservationTableColor")}
                    value={normalizeTableHex(popupColor || defaultTableColor)}
                    disabled={isTableLockedForSlot(popupTable)}
                    onChange={(event) =>
                      setPopupColor(event.target.value.toUpperCase())
                    }
                  />
                  <input
                    type="text"
                    className="form-control reservation-table-popup__color-hex"
                    value={popupColor}
                    disabled={isTableLockedForSlot(popupTable)}
                    placeholder={defaultTableColor}
                    spellCheck={false}
                    onChange={(event) => {
                      const value = event.target.value.toUpperCase();
                      if (/^#([0-9A-F]{0,6})$/i.test(value) || value === "") {
                        setPopupColor(value);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="reservation-table-popup__color-reset"
                    disabled={
                      isTableLockedForSlot(popupTable) || !popupColor
                    }
                    onClick={() => setPopupColor("")}
                    title={t("reservationTableColorReset")}
                  >
                    {t("reservationTableColorReset")}
                  </button>
                </div>
              </div>

              <div className="reservation-table-popup__grid">
                <label className="reservation-table-popup__field">
                  <span>{t("reservationTableName")}</span>
                  <input
                    className="form-control"
                    value={popupName}
                    disabled={isTableLockedForSlot(popupTable)}
                    onChange={(event) => setPopupName(event.target.value)}
                  />
                </label>
                <label className="reservation-table-popup__field">
                  <span>{t("reservationSeats")}</span>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={popupSeats}
                    disabled={isTableLockedForSlot(popupTable)}
                    onChange={(event) => setPopupSeats(event.target.value)}
                  />
                </label>
                <label className="reservation-table-popup__field">
                  <span>{t("width")}</span>
                  <input
                    type="number"
                    min="56"
                    className="form-control"
                    value={popupSize.width}
                    disabled={isTableLockedForSlot(popupTable)}
                    onChange={(event) =>
                      setPopupSize((prev) => ({ ...prev, width: event.target.value }))
                    }
                  />
                </label>
                <label className="reservation-table-popup__field">
                  <span>{t("height")}</span>
                  <input
                    type="number"
                    min="56"
                    className="form-control"
                    value={popupSize.height}
                    disabled={isTableLockedForSlot(popupTable)}
                    onChange={(event) =>
                      setPopupSize((prev) => ({ ...prev, height: event.target.value }))
                    }
                  />
                </label>
              </div>

              <div className="reservation-table-popup__actions">
                <button
                  type="button"
                  className="reservation-table-popup__btn reservation-table-popup__btn--primary"
                  disabled={submittingPopup || isTableLockedForSlot(popupTable)}
                  onClick={handleSaveTableSize}
                >
                  {submittingPopup ? t("updating") : t("reservationUpdateTable")}
                </button>
                <button
                  type="button"
                  className="reservation-table-popup__btn reservation-table-popup__btn--reserve"
                  disabled={isTableLockedForSlot(popupTable)}
                  onClick={handleOpenReserveFromTable}
                >
                  <i className="fa fa-calendar-plus-o" aria-hidden="true" />
                  {t("reservationReserveTable")}
                </button>
              </div>
            </section>
          </div>
        </div>
      )}
      </div>
    </Fragment>
  );
};

export default Reservations;